// src/support/knowledgeBase.js
// FAQ / auto-reply knowledge base for the support chatbot.
// Each entry has keywords (matched against user input), a reply, and optional follow-ups.
// Exported as a flat array so the chat engine can match against it.

const knowledgeBase = [
  // ═══════════════════════════════════════════
  // ─── TICKETS & EVENTS ─────────────────────
  // ═══════════════════════════════════════════
  {
    id: 'buy-ticket',
    keywords: ['buy ticket', 'purchase ticket', 'how to buy', 'get ticket', 'buy a ticket', 'buying tickets', 'purchase a ticket'],
    question: 'How do I buy a ticket?',
    answer:
      'To buy a ticket:\n1. Connect your wallet (MetaMask, Coinbase, or WalletConnect)\n2. Browse events on the home page\n3. Select your event and ticket type\n4. Click "Buy" and approve the PGT token transaction\n5. Your ticket NFT will appear in the "My Tickets" tab\n\nMake sure you have enough PGT tokens and a small amount of ETH for gas.',
    category: 'tickets',
  },
  {
    id: 'ticket-types',
    keywords: ['ticket type', 'vip', 'general admission', 'types of tickets', 'ticket tiers', 'early bird', 'ticket categories'],
    question: 'What ticket types are available?',
    answer:
      'Each event can offer multiple ticket types set by the organizer:\n• **General Admission** — Standard entry\n• **VIP** — Premium access, often includes perks\n• **Early Bird** — Discounted price, limited quantity\n• **Group** — Bulk purchase discounts\n\nPrices and availability vary per event. Check the event page for details.',
    category: 'tickets',
  },
  {
    id: 'view-tickets',
    keywords: ['my tickets', 'view tickets', 'find my ticket', 'where are my tickets', 'ticket collection', 'see my tickets'],
    question: 'Where can I see my tickets?',
    answer:
      'Your tickets are in the **"My Tickets"** tab. Connect your wallet and they will load automatically.\n\nTickets are NFTs stored on the blockchain, so they\'re always in your wallet even if the app is offline. You can also view them on any NFT marketplace that supports Base network.',
    category: 'tickets',
  },
  {
    id: 'ticket-not-showing',
    keywords: ['ticket not showing', 'missing ticket', 'cant see ticket', "can't find ticket", 'ticket disappeared', 'no tickets'],
    question: 'My ticket is not showing up',
    answer:
      'If your ticket isn\'t visible:\n1. Make sure you\'re connected with the **same wallet** you used to purchase\n2. Check that you\'re on the correct network (Base)\n3. Try refreshing — click the refresh icon in My Tickets\n4. Clear your browser cache and reconnect\n5. Check the transaction on BaseScan to confirm it went through\n\nIf the transaction is confirmed but the ticket still doesn\'t appear, please create a support ticket and include your wallet address and transaction hash.',
    category: 'tickets',
  },
  {
    id: 'transfer-ticket',
    keywords: ['transfer ticket', 'send ticket', 'give ticket', 'transfer nft', 'send to friend', 'gift ticket'],
    question: 'Can I transfer my ticket to someone else?',
    answer:
      'Yes! Tickets are NFTs, so you can transfer them:\n1. Go to **My Tickets**\n2. Select the ticket you want to transfer\n3. Click "Transfer" and enter the recipient\'s wallet address\n4. Confirm the transaction\n\nNote: Some events may restrict transfers. The organizer sets these rules.',
    category: 'tickets',
  },
  {
    id: 'ticket-price',
    keywords: ['ticket price', 'how much does ticket cost', 'ticket cost', 'pricing', 'cost of ticket'],
    question: 'How much do tickets cost?',
    answer:
      'Ticket prices are set by each event organizer and listed in PGT tokens. Prices vary by:\n• **Event** — Each event has its own pricing\n• **Ticket type** — VIP costs more than General Admission\n• **Early bird** — Early purchases may get discounts\n• **Coupons** — Discount codes may be available\n\nYou can see the exact price on the event page before purchasing. Gas fees (typically <$0.01 on Base) are extra.',
    category: 'tickets',
  },
  {
    id: 'ticket-limit',
    keywords: ['ticket limit', 'how many tickets', 'max tickets', 'buy multiple', 'bulk buy', 'batch purchase'],
    question: 'Is there a limit on how many tickets I can buy?',
    answer:
      'Ticket limits are set per event by the organizer:\n• **Per wallet** — Some events limit how many tickets one wallet can buy\n• **Total supply** — Each ticket type has a maximum supply\n• **Batch purchase** — You can buy multiple tickets at once (up to the limit)\n\nBatch purchases of 3+ tickets earn bonus XP! Check the event page for specific limits.',
    category: 'tickets',
  },
  {
    id: 'ticket-validity',
    keywords: ['ticket valid', 'is my ticket valid', 'expired ticket', 'ticket expiry', 'how long valid'],
    question: 'How long is my ticket valid?',
    answer:
      'Your ticket is valid until the event ends (or the organizer\'s specified end time). After the event:\n• The ticket NFT remains in your wallet as a collectible/proof of attendance\n• It can no longer be used for entry\n• It may still have resale value on the marketplace as a collectible\n\nThe QR code for entry is only active during the event\'s check-in window.',
    category: 'tickets',
  },

  // ═══════════════════════════════════════════
  // ─── REFUNDS ──────────────────────────────
  // ═══════════════════════════════════════════
  {
    id: 'refund',
    keywords: ['refund', 'get money back', 'return ticket', 'cancel ticket', 'refund policy', 'want refund'],
    question: 'How do I get a refund?',
    answer:
      'Refund policies are set by each event organizer. Generally:\n• **Before the event** — Refunds may be available depending on the organizer\'s policy\n• **Cancelled events** — Automatic refunds are processed within 48 hours\n• **After the event** — Refunds are typically not available\n\nTo request a refund, create a support ticket with your ticket ID and reason. Our team will review it within 24 hours.',
    category: 'refund',
  },
  {
    id: 'refund-time',
    keywords: ['refund how long', 'when refund', 'refund time', 'waiting for refund', 'refund status'],
    question: 'How long does a refund take?',
    answer:
      'Refund processing times:\n• **Approved refunds** — PGT tokens are returned within 1-2 business days\n• **Cancelled events** — Automatic refund within 48 hours\n• **Review required** — Our team responds within 24 hours\n\nYou\'ll see the PGT tokens back in your wallet once processed. Check your ticket\'s status in My Tickets or in your Support Tickets.',
    category: 'refund',
  },
  {
    id: 'partial-refund',
    keywords: ['partial refund', 'some money back', 'half refund', 'late cancellation'],
    question: 'Can I get a partial refund?',
    answer:
      'Partial refunds depend on the organizer\'s policy:\n• **Early cancellation** — May receive a full refund\n• **Late cancellation** — Some organizers offer partial refunds (e.g., 50%)\n• **Service fees** — Platform fees may or may not be refundable\n\nCreate a support ticket explaining your situation, and our team will check the event\'s refund terms.',
    category: 'refund',
  },

  // ═══════════════════════════════════════════
  // ─── WALLET & CONNECTION ──────────────────
  // ═══════════════════════════════════════════
  {
    id: 'connect-wallet',
    keywords: ['connect wallet', 'how to connect', 'metamask', 'wallet connect', 'coinbase wallet', 'login', 'sign in'],
    question: 'How do I connect my wallet?',
    answer:
      'We support three wallet options:\n\n**MetaMask:**\n1. Install the MetaMask browser extension\n2. Click "MetaMask" on our site\n3. Approve the connection\n\n**WalletConnect:**\n1. Click "WalletConnect"\n2. Scan the QR code with your mobile wallet\n\n**Coinbase Wallet:**\n1. Click "Coinbase Wallet"\n2. Approve in your Coinbase Wallet app\n\nMake sure you\'re on the Base network.',
    category: 'account',
  },
  {
    id: 'wrong-network',
    keywords: ['wrong network', 'switch network', 'change network', 'base network', 'not on base', 'network error'],
    question: 'I\'m on the wrong network',
    answer:
      'This app runs on **Base** (chain ID 8453). To switch:\n1. Your wallet should prompt you automatically when you connect\n2. If not, open MetaMask → click the network dropdown → select "Base"\n3. If Base isn\'t listed, click "Add Network" and use:\n   - Network: Base\n   - RPC: https://mainnet.base.org\n   - Chain ID: 8453\n   - Currency: ETH\n   - Explorer: https://basescan.org',
    category: 'account',
  },
  {
    id: 'wallet-disconnected',
    keywords: ['wallet disconnected', 'keeps disconnecting', 'connection lost', 'wallet wont connect', 'reconnect'],
    question: 'My wallet keeps disconnecting',
    answer:
      'Try these steps:\n1. **Clear site data** — In your browser settings, clear cookies/cache for this site\n2. **Re-approve** — Disconnect from the wallet extension, then reconnect\n3. **Check browser** — Make sure your wallet extension is up to date\n4. **Try another browser** — Chrome and Brave work best with MetaMask\n5. **Disable other extensions** — Some extensions can conflict with wallet connections\n\nIf the issue persists, create a support ticket with your browser name and wallet type.',
    category: 'account',
  },
  {
    id: 'multiple-wallets',
    keywords: ['multiple wallets', 'two wallets', 'different wallet', 'switch wallet', 'change wallet', 'wrong wallet'],
    question: 'Can I use multiple wallets?',
    answer:
      'Yes, but each wallet has its own tickets and PGT balance. Important notes:\n• **Tickets are per-wallet** — Connect the wallet that holds the tickets you want to see\n• **Switching** — Disconnect your current wallet, then connect the other one\n• **MetaMask** — You can switch accounts within MetaMask to use different wallets\n• **Transfer** — You can transfer tickets between your wallets using the Transfer feature\n\nTip: Use one primary wallet for all purchases to keep your XP and loyalty progress in one place.',
    category: 'account',
  },
  {
    id: 'no-wallet',
    keywords: ['no wallet', 'dont have wallet', "don't have wallet", 'need wallet', 'what wallet', 'which wallet', 'new to crypto'],
    question: 'I don\'t have a wallet',
    answer:
      'You need a crypto wallet to use the platform. Here\'s the easiest way to get started:\n\n**Recommended: MetaMask**\n1. Go to metamask.io and install the browser extension\n2. Create a new wallet (follow the setup wizard)\n3. **Save your seed phrase** — write it down and keep it safe!\n4. Come back to our site and click "MetaMask" to connect\n\n**Mobile:** Install MetaMask or Coinbase Wallet from your app store.\n\nYou\'ll also need a small amount of ETH on Base for gas fees and PGT tokens to buy tickets.',
    category: 'account',
  },

  // ═══════════════════════════════════════════
  // ─── PGT TOKEN ────────────────────────────
  // ═══════════════════════════════════════════
  {
    id: 'what-is-pgt',
    keywords: ['what is pgt', 'pgt token', 'about pgt', 'pgt coin', 'pgt currency'],
    question: 'What is PGT?',
    answer:
      'PGT (PreGame Token) is the utility token used across our ecosystem:\n• **Buy tickets** — All ticket purchases use PGT\n• **Marketplace** — Buy and sell tickets on the secondary market\n• **Rewards** — Earn PGT through loyalty programs and XP\n• **Governance** — Future voting on platform decisions\n\nPGT is an ERC-20 token on the Base network.',
    category: 'account',
  },
  {
    id: 'buy-pgt',
    keywords: ['buy pgt', 'get pgt', 'where to buy pgt', 'swap for pgt', 'purchase pgt', 'need pgt'],
    question: 'How do I get PGT tokens?',
    answer:
      'You can get PGT tokens several ways:\n1. **Swap** — Use the built-in swap feature to exchange ETH for PGT\n2. **Buy directly** — If available, use the "Buy PGT" button on the home page\n3. **Earn** — Complete activities to earn PGT through XP and loyalty rewards\n4. **Transfer** — Receive PGT from another wallet\n\nYou\'ll need a small amount of ETH for gas fees on all transactions.',
    category: 'account',
  },
  {
    id: 'pgt-balance',
    keywords: ['pgt balance', 'check balance', 'how much pgt', 'see my pgt', 'where is my pgt'],
    question: 'Where can I see my PGT balance?',
    answer:
      'Your PGT balance is displayed at the top of the page once your wallet is connected. It shows your current PGT amount.\n\nYou can also check your balance:\n• In MetaMask — Add PGT as a custom token using the contract address\n• On BaseScan — Search your wallet address\n• In the app — The balance auto-refreshes every 30 seconds',
    category: 'account',
  },
  {
    id: 'pgt-value',
    keywords: ['pgt value', 'pgt price', 'how much is pgt worth', 'pgt to usd', 'pgt exchange rate'],
    question: 'What is PGT worth?',
    answer:
      'PGT\'s value fluctuates based on supply and demand:\n• **Current price** — Check the swap interface for the live ETH/PGT rate\n• **USD value** — Approximate USD value is shown on the app (based on ETH price)\n• **Market-driven** — The price is determined by the liquidity pool\n\nPGT is a utility token primarily used within our ecosystem for ticket purchases, marketplace trading, and rewards.',
    category: 'account',
  },
  {
    id: 'pgt-swap-failed',
    keywords: ['swap failed', 'cant swap', "can't swap", 'swap error', 'exchange failed', 'swap not working'],
    question: 'My PGT swap failed',
    answer:
      'Common reasons for swap failures:\n1. **Insufficient ETH** — You need ETH to swap for PGT (and for gas)\n2. **Slippage too low** — Increase slippage tolerance in settings (try 1-3%)\n3. **Price impact** — Very large swaps may exceed the liquidity pool capacity\n4. **Gas too low** — Try increasing the gas limit\n5. **Network congestion** — Wait a few minutes and try again\n\nIf the swap was deducted but you didn\'t receive PGT, check the transaction on BaseScan and create a support ticket with the tx hash.',
    category: 'technical',
  },

  // ═══════════════════════════════════════════
  // ─── MARKETPLACE ──────────────────────────
  // ═══════════════════════════════════════════
  {
    id: 'marketplace',
    keywords: ['marketplace', 'resell', 'sell ticket', 'list ticket', 'secondary market', 'buy resale'],
    question: 'How does the ticket marketplace work?',
    answer:
      'The marketplace lets you buy and sell tickets on the secondary market:\n\n**To sell:**\n1. Go to My Tickets → select a ticket\n2. Click "List on Marketplace"\n3. Set your price in PGT\n4. Approve the marketplace contract (one-time)\n5. Confirm the listing\n\n**To buy:**\n1. Browse the Marketplace tab\n2. Find a ticket you want\n3. Click "Buy" and confirm\n\nA small platform fee applies to sales.',
    category: 'tickets',
  },
  {
    id: 'marketplace-fee',
    keywords: ['marketplace fee', 'selling fee', 'platform fee', 'commission', 'how much fee'],
    question: 'What are the marketplace fees?',
    answer:
      'Marketplace fees:\n• **Platform fee** — A small percentage of the sale price goes to the platform\n• **Gas fee** — Standard blockchain transaction cost (paid in ETH)\n• **No buyer fee** — Buyers only pay the listed price + gas\n\nThe exact platform fee percentage is set by the event organizer and displayed before you confirm a listing.',
    category: 'tickets',
  },
  {
    id: 'marketplace-listing-not-showing',
    keywords: ['listing not showing', 'my listing', 'where is my listing', 'listed but not visible', 'marketplace not loading'],
    question: 'My marketplace listing is not showing',
    answer:
      'If your listing isn\'t appearing:\n1. **Approval pending** — Check if the marketplace approval transaction was confirmed\n2. **Refresh** — Pull to refresh or reload the marketplace page\n3. **Network** — Make sure you\'re on Base network\n4. **Transaction confirmed** — Check BaseScan for your listing transaction\n5. **Event filter** — Make sure you\'re browsing the correct event\n\nIf the listing transaction is confirmed but not showing, wait a few minutes — there can be a short indexing delay.',
    category: 'tickets',
  },
  {
    id: 'cancel-listing',
    keywords: ['cancel listing', 'remove listing', 'unlist ticket', 'delist', 'take off marketplace', 'stop selling'],
    question: 'How do I cancel a marketplace listing?',
    answer:
      'To cancel a listing:\n1. Go to **Marketplace** → **My Listings** (or My Tickets)\n2. Find the listed ticket\n3. Click "Cancel Listing"\n4. Confirm the transaction\n\nYour ticket NFT will be returned to your wallet. There is a small gas fee for the cancellation transaction.',
    category: 'tickets',
  },
  {
    id: 'marketplace-price',
    keywords: ['set price', 'listing price', 'how much to charge', 'fair price', 'pricing my ticket'],
    question: 'How should I price my ticket on the marketplace?',
    answer:
      'Pricing tips:\n• **Original price** — You can see what you paid on the ticket detail page\n• **Market demand** — Check what similar tickets are listed for\n• **Event date** — Tickets may be worth more as the event approaches (or less if oversupplied)\n• **Ticket type** — VIP tickets typically have higher resale value\n\nYou set the price in PGT. The buyer pays your price + gas. A platform fee is deducted from the sale amount.',
    category: 'tickets',
  },

  // ═══════════════════════════════════════════
  // ─── QR CODES & CHECK-IN ──────────────────
  // ═══════════════════════════════════════════
  {
    id: 'qr-code',
    keywords: ['qr code', 'scan ticket', 'check in', 'checkin', 'entry', 'show ticket', 'event entry'],
    question: 'How do I use my ticket at the event?',
    answer:
      'At the event entrance:\n1. Open the app and go to **My Tickets**\n2. Select your ticket for this event\n3. Show the **QR code** to the staff member\n4. They\'ll scan it with the staff scanner\n5. You\'re in!\n\nThe QR code is cryptographically signed and can only be used once. Screenshots won\'t work — the code must be scanned from the app.',
    category: 'event_inquiry',
  },
  {
    id: 'qr-not-working',
    keywords: ['qr not working', 'scan failed', 'invalid qr', 'qr error', 'cant scan', "won't scan"],
    question: 'My QR code isn\'t working',
    answer:
      'If your QR code fails to scan:\n1. **Increase brightness** — Set your phone screen to maximum\n2. **Remove screen protector** — Glare can block scanning\n3. **Refresh** — Pull down to refresh the ticket and generate a new QR\n4. **Check wallet** — Make sure you\'re connected with the correct wallet\n5. **Internet connection** — The QR needs to be verified online\n\nIf it still fails, find a staff member — they can verify your ticket manually with your wallet address.',
    category: 'event_inquiry',
  },
  {
    id: 'already-checked-in',
    keywords: ['already checked in', 'already scanned', 'ticket used', 'already used', 'duplicate scan', 'scanned twice'],
    question: 'My ticket says already checked in',
    answer:
      'If your ticket shows "already checked in" but you haven\'t entered the event:\n1. **Someone else used it** — If you transferred/shared your ticket, the recipient may have checked in\n2. **Staff error** — Ask the staff to check the scan log — they can see the time and device that scanned it\n3. **Technical glitch** — Rare, but staff can override with manual verification of your wallet ownership\n\nFind the event manager at the entrance for immediate resolution.',
    category: 'event_inquiry',
  },

  // ═══════════════════════════════════════════
  // ─── XP & LOYALTY ─────────────────────────
  // ═══════════════════════════════════════════
  {
    id: 'xp-system',
    keywords: ['xp', 'experience points', 'level up', 'levels', 'xp system', 'earn xp'],
    question: 'How does the XP system work?',
    answer:
      'Earn XP (experience points) by participating in the ecosystem:\n• **Buy a ticket** — 50 XP\n• **First purchase** — 100 XP bonus\n• **Batch purchase (3+)** — 20 XP per ticket\n• **Check in at event** — 30 XP\n• **Referral** — 75 XP when someone you referred buys\n\nAs you level up, you unlock rewards and perks. Check your XP and level in the "XP & Levels" tab.',
    category: 'event_inquiry',
  },
  {
    id: 'xp-not-updating',
    keywords: ['xp not updating', 'xp missing', 'didnt get xp', "didn't get xp", 'xp not added', 'where is my xp'],
    question: 'My XP didn\'t update',
    answer:
      'XP is awarded when transactions are confirmed on-chain. If your XP hasn\'t updated:\n1. **Wait a moment** — XP updates after the transaction is confirmed (~2 seconds on Base)\n2. **Refresh** — Reload the XP tab\n3. **Check eligibility** — Some actions only award XP once (e.g., first purchase bonus)\n4. **Transaction status** — Make sure the purchase/action was successful\n\nIf you believe XP is missing, note your wallet address and the transaction hash and create a support ticket.',
    category: 'event_inquiry',
  },
  {
    id: 'loyalty',
    keywords: ['loyalty', 'rewards', 'loyalty program', 'points', 'loyalty rewards', 'earn rewards', 'loyalty tier'],
    question: 'What is the loyalty program?',
    answer:
      'The loyalty program rewards frequent users:\n• **Tiered rewards** — Bronze, Silver, Gold, Platinum tiers\n• **Volume milestones** — Bonuses for buying multiple tickets\n• **Streak tracking** — Attend consecutive events for multipliers\n• **Referral bonuses** — Earn PGT for bringing new users\n\nYour loyalty tier determines your discount on future purchases and access to exclusive events.',
    category: 'event_inquiry',
  },
  {
    id: 'loyalty-tier',
    keywords: ['loyalty tier', 'what tier', 'my tier', 'tier level', 'how to level up', 'tier requirements'],
    question: 'How do loyalty tiers work?',
    answer:
      'Loyalty tiers are based on your cumulative activity:\n\n• **Bronze** — Starting tier for all new users\n• **Silver** — After 3 ticket purchases or 500 PGT spent\n• **Gold** — After 10 purchases or 2,000 PGT spent\n• **Platinum** — After 25 purchases or 5,000 PGT spent\n\nHigher tiers unlock better discounts, early access to tickets, and exclusive events. Your tier progress is visible in the Loyalty section.',
    category: 'event_inquiry',
  },
  {
    id: 'referral',
    keywords: ['referral', 'refer friend', 'referral code', 'invite', 'referral bonus', 'refer a friend', 'invite friend'],
    question: 'How does the referral program work?',
    answer:
      'Earn rewards by referring friends:\n1. **Get your link** — Find your unique referral link in the Loyalty/XP section\n2. **Share it** — Send it to friends\n3. **They sign up** — When they connect and buy their first ticket\n4. **You earn** — 75 XP + bonus PGT for each successful referral\n\nThere\'s no limit to how many friends you can refer. Referrals also count toward your loyalty tier progression.',
    category: 'event_inquiry',
  },

  // ═══════════════════════════════════════════
  // ─── COUPONS & DISCOUNTS ──────────────────
  // ═══════════════════════════════════════════
  {
    id: 'coupons',
    keywords: ['coupon', 'discount', 'promo code', 'discount code', 'coupon code', 'promo'],
    question: 'How do I use a coupon code?',
    answer:
      'If you have a coupon code:\n1. Go to the ticket purchase page\n2. Enter the code in the "Coupon" field\n3. Click "Apply" — the discount will be reflected in the price\n4. Complete your purchase at the reduced price\n\nCoupons may have restrictions: expiry date, specific events, minimum purchase, or usage limits. The app will show any restrictions when you apply the code.',
    category: 'tickets',
  },
  {
    id: 'coupon-not-working',
    keywords: ['coupon not working', 'invalid coupon', 'coupon expired', 'code doesnt work', "code doesn't work", 'discount not applied'],
    question: 'My coupon code isn\'t working',
    answer:
      'Common reasons a coupon might not work:\n• **Expired** — The coupon may have passed its expiry date\n• **Usage limit reached** — The coupon may have been used its maximum number of times\n• **Event-specific** — Some coupons only work for certain events\n• **Minimum purchase** — The coupon may require a minimum spend\n• **Already used** — Some coupons are single-use per wallet\n• **Case sensitive** — Try entering the code exactly as given\n\nIf you believe the code should work, create a support ticket with the coupon code and the error message.',
    category: 'tickets',
  },
  {
    id: 'find-coupons',
    keywords: ['find coupon', 'where to get coupon', 'get discount', 'free coupon', 'how to get coupon'],
    question: 'Where can I find coupon codes?',
    answer:
      'Coupon codes are distributed through:\n• **Event organizers** — Follow events for exclusive codes\n• **Social media** — Check our official channels for promotions\n• **Loyalty rewards** — Higher loyalty tiers may receive exclusive coupons\n• **Referral bonuses** — Some referral milestones reward discount codes\n• **Email newsletters** — Subscribe for periodic offers\n\nCoupons are on-chain via our smart contract, ensuring they\'re genuine and transparent.',
    category: 'tickets',
  },

  // ═══════════════════════════════════════════
  // ─── EVENT INFO ───────────────────────────
  // ═══════════════════════════════════════════
  {
    id: 'event-cancelled',
    keywords: ['event cancelled', 'event canceled', 'is event happening', 'event postponed', 'event rescheduled'],
    question: 'What happens if an event is cancelled?',
    answer:
      'If an event is cancelled:\n• **Automatic refund** — PGT tokens are returned to your wallet within 48 hours\n• **Notification** — You\'ll see a notification in the app\n• **Rescheduled events** — Your ticket remains valid for the new date (unless you request a refund)\n\nCheck the event page for the latest status updates from the organizer.',
    category: 'event_inquiry',
  },
  {
    id: 'event-details',
    keywords: ['event details', 'event info', 'when is event', 'where is event', 'event location', 'event date', 'event time'],
    question: 'Where can I find event details?',
    answer:
      'All event details are on the event page:\n• **Date & time** — Listed at the top\n• **Location** — Address and map link\n• **Description** — Full event details from the organizer\n• **Ticket types** — Available tiers and prices\n• **Sales window** — When tickets go on/off sale\n\nBrowse events on the home page or use the search to find specific events.',
    category: 'event_inquiry',
  },
  {
    id: 'sold-out',
    keywords: ['sold out', 'no tickets available', 'tickets gone', 'out of stock', 'cant buy ticket', 'all sold'],
    question: 'The event is sold out',
    answer:
      'If an event is sold out:\n• **Marketplace** — Check the ticket marketplace for resale tickets from other users\n• **Waitlist** — Some events have a waitlist (check the event page)\n• **Another ticket type** — VIP or other tiers might still be available\n• **Follow the event** — Tickets sometimes get released if sales are extended\n\nResale tickets on our marketplace are verified on-chain, so you\'re guaranteed an authentic ticket.',
    category: 'event_inquiry',
  },
  {
    id: 'upcoming-events',
    keywords: ['upcoming events', 'next event', 'what events', 'event schedule', 'event calendar', 'new events'],
    question: 'How do I find upcoming events?',
    answer:
      'Browse upcoming events:\n• **Home page** — All active events are displayed with the slider\n• **Filter** — Use filters to narrow by date, location, or type\n• **Event pages** — Each event has a dedicated page with full details\n\nNew events are added by organizers and appear automatically. Follow our social media for announcements about major events.',
    category: 'event_inquiry',
  },

  // ═══════════════════════════════════════════
  // ─── TECHNICAL / TROUBLESHOOTING ──────────
  // ═══════════════════════════════════════════
  {
    id: 'transaction-failed',
    keywords: ['transaction failed', 'tx failed', 'purchase failed', 'error buying', 'transaction error', 'reverted'],
    question: 'My transaction failed',
    answer:
      'Common reasons for failed transactions:\n1. **Insufficient PGT** — Check your balance covers the ticket price\n2. **Insufficient ETH** — You need ETH for gas fees\n3. **Sold out** — The ticket type may have sold out while you were buying\n4. **Sales closed** — The ticket sale window may have ended\n5. **Network congestion** — Try increasing the gas limit in your wallet\n\nCheck the transaction on BaseScan for the specific revert reason. If you were charged but didn\'t receive a ticket, create a support ticket with the transaction hash.',
    category: 'technical',
  },
  {
    id: 'gas-fees',
    keywords: ['gas fee', 'gas', 'transaction cost', 'how much gas', 'gas price', 'eth for gas'],
    question: 'How much are gas fees?',
    answer:
      'Gas fees on Base are very low — typically under $0.01 per transaction. You\'ll need a small amount of ETH in your wallet to cover gas.\n\nThe app shows the estimated gas cost before you confirm. If gas seems high, wait a few minutes — Base network fees fluctuate but are generally very affordable.',
    category: 'technical',
  },
  {
    id: 'slow-loading',
    keywords: ['slow', 'loading', 'takes long', 'not loading', 'spinner', 'stuck loading', 'page frozen'],
    question: 'The app is loading slowly',
    answer:
      'Try these fixes:\n1. **Refresh the page** — Hard refresh with Ctrl+Shift+R (Cmd+Shift+R on Mac)\n2. **Check internet** — Ensure stable connection\n3. **Clear cache** — Clear browser cache and cookies for this site\n4. **Try another browser** — Chrome or Brave recommended\n5. **Disable extensions** — Ad blockers can interfere\n\nIf the app consistently loads slowly, it might be RPC congestion — try again in a few minutes.',
    category: 'technical',
  },
  {
    id: 'mobile-support',
    keywords: ['mobile', 'phone', 'mobile app', 'android', 'iphone', 'ios', 'mobile version'],
    question: 'Is there a mobile app?',
    answer:
      'Yes! We have a mobile app for both iOS and Android:\n• **Download** — Available on App Store and Google Play\n• **Features** — Buy tickets, view QR codes, manage wallet, marketplace\n• **Wallet** — Uses WalletConnect to connect your mobile wallet\n\nYou can also use the web app on your mobile browser — it\'s fully responsive.',
    category: 'technical',
  },
  {
    id: 'pending-transaction',
    keywords: ['pending transaction', 'transaction stuck', 'tx pending', 'waiting for confirmation', 'transaction not confirmed'],
    question: 'My transaction is stuck/pending',
    answer:
      'If a transaction is stuck pending:\n1. **Wait** — Most Base transactions confirm in 2-3 seconds. Give it up to a minute\n2. **Check BaseScan** — Look up your address to see the transaction status\n3. **Speed up** — In MetaMask, you can click "Speed Up" on a pending transaction\n4. **Nonce issue** — If you have multiple stuck transactions, you may need to reset your nonce in MetaMask (Settings → Advanced → Reset Account)\n\nIf the transaction has been pending for more than 5 minutes, it likely failed silently. Try again.',
    category: 'technical',
  },
  {
    id: 'browser-compatibility',
    keywords: ['browser', 'chrome', 'firefox', 'safari', 'brave', 'which browser', 'browser not supported'],
    question: 'Which browsers are supported?',
    answer:
      'Supported browsers:\n• **Chrome** ✅ Best experience, fully supported\n• **Brave** ✅ Fully supported (with built-in wallet)\n• **Firefox** ✅ Supported with MetaMask extension\n• **Safari** ⚠️ Limited — WalletConnect only, no MetaMask extension\n• **Edge** ✅ Supported with MetaMask extension\n\nFor the best experience, we recommend Chrome or Brave on desktop. On mobile, use the MetaMask in-app browser or our native mobile app.',
    category: 'technical',
  },
  {
    id: 'clear-cache',
    keywords: ['clear cache', 'reset app', 'app glitch', 'display error', 'ui broken', 'page broken'],
    question: 'How do I clear cache / reset the app?',
    answer:
      'To reset the app:\n1. **Hard refresh** — Ctrl+Shift+R (Windows) or Cmd+Shift+R (Mac)\n2. **Clear site data** — In browser settings, find our site and clear cookies + cache\n3. **Disconnect wallet** — Click disconnect, then reconnect\n4. **Try incognito** — Open in an incognito/private window to test\n\nThis won\'t affect your tickets or PGT — those are on the blockchain and always safe.',
    category: 'technical',
  },

  // ═══════════════════════════════════════════
  // ─── SECURITY ─────────────────────────────
  // ═══════════════════════════════════════════
  {
    id: 'security',
    keywords: ['security', 'is it safe', 'safe to use', 'scam', 'fraud', 'fake ticket', 'hack'],
    question: 'Is the platform secure?',
    answer:
      'Yes, we use multiple security layers:\n• **Blockchain-based** — Tickets are NFTs, immutable and verifiable\n• **Smart contracts** — Audited code handles all transactions\n• **Signed QR codes** — Cryptographic signatures prevent forgery\n• **No password storage** — Authentication via wallet signatures\n• **Admin security** — EIP-712 signatures for all admin operations\n\nAlways verify you\'re on the official site and never share your wallet seed phrase.',
    category: 'account',
  },
  {
    id: 'fake-ticket',
    keywords: ['fake ticket', 'counterfeit', 'stolen ticket', 'ticket fraud', 'duplicate ticket', 'ticket authentic'],
    question: 'How do I know a ticket is real?',
    answer:
      'Every ticket is an NFT on the blockchain, making counterfeiting impossible:\n• **Verify on-chain** — Check the token ID on BaseScan\n• **QR validation** — QR codes are cryptographically signed and verified at scan\n• **One-time use** — Each ticket can only be scanned once for entry\n• **Marketplace safe** — Buying through our marketplace guarantees authenticity\n\nNever buy tickets outside the official marketplace — direct NFT transfers can\'t be verified for event validity.',
    category: 'account',
  },
  {
    id: 'seed-phrase',
    keywords: ['seed phrase', 'private key', 'recovery phrase', 'lost seed phrase', 'someone asking for seed', 'share private key'],
    question: 'Someone is asking for my seed phrase / private key',
    answer:
      '⚠️ **NEVER share your seed phrase or private key with anyone!**\n\nImportant security rules:\n• **We will NEVER ask** for your seed phrase or private key\n• **No legit service** will ever ask for this information\n• **Scam alert** — Anyone asking is trying to steal your funds\n• **Support team** only needs your public wallet address (0x...)\n\nIf you accidentally shared your seed phrase, immediately transfer all assets to a new wallet.',
    category: 'account',
  },
  {
    id: 'suspicious-activity',
    keywords: ['suspicious', 'hacked', 'unauthorized', 'someone used my wallet', 'stolen funds', 'tokens missing'],
    question: 'I think my wallet was compromised',
    answer:
      'If you suspect unauthorized activity:\n1. **Don\'t panic** — But act quickly\n2. **Transfer assets** — Move remaining PGT and NFTs to a new, secure wallet\n3. **Revoke approvals** — Use revoke.cash to revoke all token approvals from the compromised wallet\n4. **New wallet** — Create a new wallet with a fresh seed phrase\n5. **Report** — Create a support ticket with your wallet address and details of suspicious transactions\n\nNever reuse a compromised wallet or seed phrase.',
    category: 'account',
  },

  // ═══════════════════════════════════════════
  // ─── POS / MERCHANT ───────────────────────
  // ═══════════════════════════════════════════
  {
    id: 'pos',
    keywords: ['pos', 'point of sale', 'merchant', 'accept pgt', 'terminal', 'sell at venue'],
    question: 'How does the POS system work?',
    answer:
      'The Point of Sale (POS) system lets merchants accept PGT payments at events:\n1. **Setup** — Install the POS app and configure your merchant wallet\n2. **Products** — Add your products/items with prices in PGT\n3. **Accept payment** — Show QR to customer, they scan and pay\n4. **Track sales** — Real-time dashboard shows transactions\n\nRequires a merchant license. Contact us to get started.',
    category: 'event_inquiry',
  },
  {
    id: 'pos-setup',
    keywords: ['pos setup', 'set up pos', 'configure pos', 'merchant setup', 'start accepting payments'],
    question: 'How do I set up a POS terminal?',
    answer:
      'To set up a POS terminal:\n1. **Get a license** — Purchase a merchant license through the License Manager\n2. **Install** — Open the POS app on your device (tablet or phone)\n3. **Connect wallet** — Connect your merchant wallet\n4. **Add products** — Create your product catalog with names, prices, and categories\n5. **Configure** — Set your display name, location, and receipt preferences\n6. **Start selling** — The terminal is ready to accept PGT payments\n\nMultiple terminals can be linked to a single merchant account.',
    category: 'event_inquiry',
  },
  {
    id: 'merchant-license',
    keywords: ['merchant license', 'license cost', 'how to get license', 'vendor license', 'become merchant'],
    question: 'How do I get a merchant license?',
    answer:
      'Merchant licenses are managed on-chain:\n1. **Check pricing** — Visit the License Manager page to see current prices\n2. **Purchase** — Pay the license fee in PGT\n3. **Activate** — Your license activates immediately after the transaction confirms\n4. **Features unlocked** — POS terminal, sales analytics, support module, and more\n\nLicenses are per-wallet and may be time-limited or permanent depending on the tier. Super admin can also grant licenses directly.',
    category: 'event_inquiry',
  },

  // ═══════════════════════════════════════════
  // ─── AMBASSADOR / STAFF ───────────────────
  // ═══════════════════════════════════════════
  {
    id: 'staff-scanner',
    keywords: ['staff scanner', 'scanner app', 'scan tickets', 'event staff', 'door scanner', 'check in app'],
    question: 'How does the staff scanner work?',
    answer:
      'The staff scanner is used by event personnel to verify tickets at the door:\n1. **Open** the staff scanner app on your device\n2. **Connect** your wallet (must have staff/admin role for the event)\n3. **Scan** — Point the camera at the attendee\'s QR code\n4. **Verify** — The app shows green (valid) or red (invalid/already used)\n5. **Offline mode** — Works without internet by caching ticket data\n\nThe scanner validates the cryptographic signature on each QR code to prevent fraud.',
    category: 'event_inquiry',
  },
  {
    id: 'ambassador',
    keywords: ['ambassador', 'become ambassador', 'ambassador program', 'brand ambassador', 'ambassador role'],
    question: 'What is the Ambassador program?',
    answer:
      'Ambassadors help promote the platform and earn rewards:\n• **Referral bonuses** — Higher referral rewards than standard users\n• **Exclusive access** — Early access to new events and features\n• **Commission** — Earn a percentage on ticket sales from your referrals\n• **Tier progression** — Work up from Bronze to Platinum ambassador\n\nTo apply, reach out through a support ticket or check if ambassador sign-up is available on the platform.',
    category: 'event_inquiry',
  },

  // ═══════════════════════════════════════════
  // ─── ADMIN / ORGANIZER ────────────────────
  // ═══════════════════════════════════════════
  {
    id: 'create-event',
    keywords: ['create event', 'make event', 'organize event', 'host event', 'new event', 'event organizer'],
    question: 'How do I create an event?',
    answer:
      'Event creation is managed by admins and organizers:\n1. **Admin access** — You need an admin or organizer role\n2. **Contract** — Events are created via the EventTicket smart contract\n3. **Configure** — Set event name, date, location, description\n4. **Ticket types** — Add ticket types with names, prices, and quantities\n5. **Publish** — Deploy to make tickets available for purchase\n\nIf you\'re interested in hosting an event, contact our team through a support ticket.',
    category: 'event_inquiry',
  },
  {
    id: 'admin-access',
    keywords: ['admin', 'admin panel', 'admin access', 'organizer access', 'admin login', 'manage event'],
    question: 'How do I access the admin panel?',
    answer:
      'Admin access requires an authorized wallet:\n1. **Connect** the wallet that has admin rights\n2. **Admin tab** — The "Admin" tab appears automatically for authorized wallets\n3. **Manage** — From there you can manage events, tickets, users, and support\n\nAdmin authentication uses EIP-712 signatures — no passwords needed. If you need admin access, contact the super admin.',
    category: 'other',
  },

  // ═══════════════════════════════════════════
  // ─── GENERAL / CATCH-ALL ──────────────────
  // ═══════════════════════════════════════════
  {
    id: 'contact-human',
    keywords: ['talk to human', 'real person', 'speak to someone', 'human support', 'agent', 'live chat', 'operator'],
    question: 'Can I talk to a real person?',
    answer:
      'Of course! Click **"Create Ticket"** below to open a support ticket. Our team typically responds within a few hours during business hours.\n\nInclude as much detail as possible:\n• Your wallet address\n• Transaction hash (if applicable)\n• Screenshots of any errors\n• Steps to reproduce the issue',
    category: 'other',
  },
  {
    id: 'other-help',
    keywords: ['help', 'support', 'need help', 'assistance', 'question', 'problem', 'issue'],
    question: 'I need help',
    answer:
      'I can help with:\n• **Tickets** — buying, viewing, transferring, refunds\n• **Wallet** — connecting, network, balance\n• **PGT** — buying, swapping, balance\n• **Marketplace** — listing, buying, cancelling\n• **Events** — details, check-in, QR codes\n• **Loyalty & XP** — tiers, points, referrals\n• **Coupons** — applying codes, finding discounts\n• **Technical** — errors, slow loading, transactions\n• **Security** — fraud, scams, wallet safety\n\nDescribe your issue and I\'ll try to help. If I can\'t resolve it, I\'ll create a support ticket for our team.',
    category: 'other',
  },
  {
    id: 'what-is-this',
    keywords: ['what is this', 'what is this platform', 'what does this do', 'about this app', 'what is pregame'],
    question: 'What is this platform?',
    answer:
      'This is the **PGT (PreGame Token) Ecosystem** — a blockchain-based event ticketing platform on Base:\n\n• **NFT Tickets** — Tamper-proof event tickets as NFTs\n• **PGT Token** — Utility token for all transactions\n• **Marketplace** — Buy and sell tickets on the secondary market\n• **Loyalty & XP** — Earn rewards for participation\n• **POS System** — Merchants can accept PGT at events\n• **Staff Scanner** — QR-based event check-in\n\nAll powered by smart contracts for transparency and security.',
    category: 'other',
  },
  {
    id: 'supported-chains',
    keywords: ['which chain', 'blockchain', 'ethereum', 'polygon', 'base chain', 'which network', 'multichain'],
    question: 'Which blockchain does this use?',
    answer:
      'The platform primarily runs on **Base** — an Ethereum L2 that offers:\n• **Low fees** — Gas costs under $0.01\n• **Fast** — ~2 second transaction confirmations\n• **Ethereum security** — Secured by Ethereum\'s validators\n• **EVM compatible** — Works with MetaMask and standard Ethereum wallets\n\nMulti-chain support for additional networks may be available in the future.',
    category: 'technical',
  },

  // ═══════════════════════════════════════════
  // ─── GASLESS / PAYMASTER ──────────────────
  // ═══════════════════════════════════════════
  {
    id: 'gasless-transactions',
    keywords: ['gasless', 'no gas', 'free transaction', 'paymaster', 'sponsored', 'no eth needed', 'gas sponsored'],
    question: 'Can I make transactions without paying gas?',
    answer:
      'Yes! Our platform supports **gasless transactions** via the Paymaster system:\n• **Sponsored gas** — The platform can cover gas fees for certain operations\n• **PGT Paymaster** — Pay gas using PGT tokens instead of ETH\n• **First-time users** — Some transactions may be gas-free to help new users get started\n\nNot all transactions are gasless — check the transaction confirmation screen. If gas is sponsored, it will say "Gas: Sponsored" instead of showing an ETH amount.',
    category: 'technical',
  },
  {
    id: 'paymaster-not-working',
    keywords: ['paymaster not working', 'gas not sponsored', 'still paying gas', 'paymaster error'],
    question: 'Gasless transactions are not working',
    answer:
      'If gasless transactions aren\'t working:\n• **Budget depleted** — The paymaster may have run out of sponsored gas budget\n• **Not eligible** — Only certain transaction types are sponsored\n• **Network issue** — Try refreshing and reconnecting your wallet\n• **Fallback** — You can always complete the transaction by paying a small ETH gas fee\n\nIf you believe gasless should be available but isn\'t, create a support ticket.',
    category: 'technical',
  },

  // ═══════════════════════════════════════════
  // ─── TOKEN SWAP ───────────────────────────
  // ═══════════════════════════════════════════
  {
    id: 'token-swap',
    keywords: ['swap', 'exchange', 'swap tokens', 'eth to pgt', 'pgt to eth', 'token exchange', 'dex'],
    question: 'How do I swap tokens?',
    answer:
      'To swap ETH for PGT (or vice versa):\n1. Open the **Swap** feature (available on web and mobile)\n2. Select the token you want to swap from (e.g., ETH)\n3. Enter the amount\n4. Review the exchange rate and slippage\n5. Click "Swap" and confirm in your wallet\n\nThe swap uses our on-chain liquidity pool. Rates are determined by supply and demand. A small swap fee may apply.',
    category: 'account',
  },
  {
    id: 'slippage',
    keywords: ['slippage', 'price impact', 'swap rate changed', 'price moved', 'slippage tolerance'],
    question: 'What is slippage?',
    answer:
      'Slippage is the difference between the expected swap price and the actual price at execution:\n• **Low slippage (0.1-0.5%)** — Good for small swaps, may fail if price moves\n• **Medium slippage (1-3%)** — Recommended for most swaps\n• **High slippage (>3%)** — Only needed for large swaps or volatile markets\n\nYou can adjust slippage tolerance in the swap settings. If a swap keeps failing, try increasing slippage slightly.',
    category: 'technical',
  },

  // ═══════════════════════════════════════════
  // ─── SEND & RECEIVE TOKENS ────────────────
  // ═══════════════════════════════════════════
  {
    id: 'send-tokens',
    keywords: ['send pgt', 'send tokens', 'transfer pgt', 'send to wallet', 'send to address'],
    question: 'How do I send PGT to someone?',
    answer:
      'To send PGT tokens:\n1. Go to the **Wallet** section\n2. Click **Send**\n3. Enter the recipient\'s wallet address (0x...)\n4. Enter the amount of PGT\n5. Review and confirm the transaction\n\n**Important:** Double-check the address before sending — blockchain transactions are irreversible. Make sure the recipient is on the Base network.',
    category: 'account',
  },
  {
    id: 'receive-tokens',
    keywords: ['receive pgt', 'receive tokens', 'my address', 'deposit', 'receive payment'],
    question: 'How do I receive PGT?',
    answer:
      'To receive PGT tokens:\n1. Go to the **Wallet** section\n2. Click **Receive** to see your wallet address and QR code\n3. Share your address or QR code with the sender\n4. They send PGT to your address on the **Base** network\n\nMake sure the sender is sending on Base — tokens sent on the wrong network may be lost.',
    category: 'account',
  },

  // ═══════════════════════════════════════════
  // ─── TRANSACTION HISTORY ──────────────────
  // ═══════════════════════════════════════════
  {
    id: 'transaction-history',
    keywords: ['transaction history', 'past transactions', 'purchase history', 'my transactions', 'order history'],
    question: 'Where can I see my transaction history?',
    answer:
      'You can view your transaction history in several places:\n• **Wallet tab** — Shows all PGT sends, receives, and swaps\n• **My Tickets** — Shows purchase history for tickets\n• **Marketplace** — Shows your buy/sell history\n• **BaseScan** — Search your wallet address for the complete on-chain history\n\nEach transaction links to BaseScan for detailed on-chain information.',
    category: 'account',
  },

  // ═══════════════════════════════════════════
  // ─── MOBILE APP SPECIFIC ──────────────────
  // ═══════════════════════════════════════════
  {
    id: 'mobile-wallet-create',
    keywords: ['create wallet mobile', 'new wallet mobile', 'mobile wallet setup', 'mobile onboarding'],
    question: 'How do I create a wallet on the mobile app?',
    answer:
      'In the mobile app:\n1. Open the app and tap **Get Started**\n2. Choose **Create New Wallet**\n3. Set a **PIN** (4-6 digits) for app security\n4. **Write down your recovery phrase** — 12 words, keep them safe!\n5. Confirm the recovery phrase\n6. Your wallet is ready!\n\nYou can also choose **Import Wallet** if you already have a wallet from MetaMask or another app.',
    category: 'account',
  },
  {
    id: 'mobile-import-wallet',
    keywords: ['import wallet', 'import seed phrase', 'restore wallet', 'recover wallet', 'use existing wallet mobile'],
    question: 'How do I import my existing wallet to the mobile app?',
    answer:
      'To import your existing wallet:\n1. Open the mobile app\n2. Tap **Import Wallet**\n3. Enter your 12 or 24-word recovery phrase\n4. Set a PIN for the app\n5. Your wallet and all assets will be restored\n\n**Security note:** Only enter your recovery phrase in trusted apps. The mobile app stores it encrypted on your device.',
    category: 'account',
  },
  {
    id: 'mobile-pin',
    keywords: ['pin', 'app pin', 'lock app', 'change pin', 'forgot pin', 'pin code', 'app security'],
    question: 'How do I set up or change my app PIN?',
    answer:
      'The mobile app PIN adds a layer of security:\n• **Set PIN** — Created during wallet setup\n• **Change PIN** — Settings → Security → Change PIN\n• **Forgot PIN** — You\'ll need to re-import your wallet using your recovery phrase\n• **Biometrics** — Enable Face ID / fingerprint in Security settings\n\nThe PIN protects app access but doesn\'t replace your wallet recovery phrase.',
    category: 'account',
  },

  // ═══════════════════════════════════════════
  // ─── NOTIFICATIONS ────────────────────────
  // ═══════════════════════════════════════════
  {
    id: 'notifications',
    keywords: ['notifications', 'alerts', 'push notifications', 'notification settings', 'email notifications'],
    question: 'How do notifications work?',
    answer:
      'The platform sends notifications for:\n• **Ticket purchased** — Confirmation after buying a ticket\n• **Marketplace activity** — When your listing sells or you receive an offer\n• **Event reminders** — Before an event you have tickets for\n• **Support updates** — When your support ticket gets a response\n• **XP milestones** — When you level up or earn rewards\n\nManage notification preferences in **Settings → Notifications**. Mobile push notifications require enabling them in your device settings.',
    category: 'technical',
  },

  // ═══════════════════════════════════════════
  // ─── ANALYTICS & REPORTS ──────────────────
  // ═══════════════════════════════════════════
  {
    id: 'analytics',
    keywords: ['analytics', 'reports', 'statistics', 'dashboard', 'sales data', 'event analytics'],
    question: 'Where can I see analytics and reports?',
    answer:
      'Analytics are available for different user roles:\n\n**Event Organizers / Admins:**\n• Event performance — ticket sales, revenue, attendance rate\n• User analytics — new vs returning buyers\n• Sales forecasting — predicted sales trends\n• Export reports — Download CSV data\n\n**Merchants:**\n• POS sales dashboard — real-time transaction data\n• Product performance — which items sell best\n• Coupon redemption reports\n\nAccess analytics from the **Admin** tab or **Merchant Dashboard**.',
    category: 'other',
  },

  // ═══════════════════════════════════════════
  // ─── ROLES & SUB-ADMINS ───────────────────
  // ═══════════════════════════════════════════
  {
    id: 'roles',
    keywords: ['roles', 'permissions', 'role management', 'sub admin', 'subadmin', 'assign role', 'manager role'],
    question: 'How do user roles work?',
    answer:
      'The platform has a role hierarchy:\n• **Super Admin** — Full system control, manages all ecosystems\n• **Admin** — Manages events, tickets, users for their ecosystem\n• **Sub-Admin** — Limited admin privileges assigned by an admin\n• **Manager** — Can manage specific events or operations\n• **Staff** — Can scan tickets and check in attendees\n• **User** — Standard user (buy tickets, use marketplace)\n\nAdmins can assign roles from the Admin panel → Roles section.',
    category: 'other',
  },
  {
    id: 'subadmin',
    keywords: ['sub admin', 'subadmin', 'add admin', 'create admin', 'give admin access', 'delegate'],
    question: 'How do I add a sub-admin?',
    answer:
      'To add a sub-admin (requires admin access):\n1. Go to **Admin** → **Sub-Admins**\n2. Click **Add Sub-Admin**\n3. Enter the person\'s wallet address\n4. Select their permissions (which actions they can perform)\n5. Confirm with your admin signature\n\nSub-admins can help manage events, respond to support tickets, and handle day-to-day operations without full admin access.',
    category: 'other',
  },

  // ═══════════════════════════════════════════
  // ─── ADD TO WALLET / NFT COLLECTIBLE ──────
  // ═══════════════════════════════════════════
  {
    id: 'add-to-wallet',
    keywords: ['add to wallet', 'save ticket', 'apple wallet', 'google wallet', 'wallet pass', 'save to phone'],
    question: 'Can I add my ticket to my phone wallet?',
    answer:
      'Yes! After purchasing a ticket:\n1. Go to **My Tickets** and select your ticket\n2. Look for the **"Add to Wallet"** button\n3. Tap it to save the ticket pass to Apple Wallet or Google Wallet\n\nThis creates a convenient pass with event details and QR code. Note: The on-chain NFT is still the authoritative ticket — the wallet pass is for convenience.',
    category: 'tickets',
  },
  {
    id: 'nft-collectible',
    keywords: ['collectible', 'nft after event', 'keep nft', 'proof of attendance', 'poap', 'souvenir'],
    question: 'Can I keep my ticket NFT after the event?',
    answer:
      'Yes! Your ticket NFT stays in your wallet forever as a digital collectible:\n• **Proof of attendance** — Verifiable on-chain that you attended the event\n• **Collectible value** — Some event NFTs may gain value over time\n• **Trading** — You can list past-event NFTs on the marketplace\n• **Metadata** — Includes event name, date, venue, and your ticket type\n\nThink of it as a digital souvenir from every event you attend.',
    category: 'tickets',
  },

  // ═══════════════════════════════════════════
  // ─── VENUE & EVENT MAP ────────────────────
  // ═══════════════════════════════════════════
  {
    id: 'venue-map',
    keywords: ['venue map', 'event map', 'find venue', 'directions', 'navigation', 'where is venue', 'events near me'],
    question: 'How do I find event venues?',
    answer:
      'Find event locations easily:\n• **Event page** — Each event shows venue address with a map link\n• **Events Near Me** — Use the location-based feature to find events nearby\n• **Events Map** — Browse events on an interactive map\n• **Directions** — Click the address to open in Google Maps / Apple Maps\n\nThe Events Near Me feature requires location permission in your browser or mobile app.',
    category: 'event_inquiry',
  },

  // ═══════════════════════════════════════════
  // ─── OFFLINE MODE ─────────────────────────
  // ═══════════════════════════════════════════
  {
    id: 'offline-mode',
    keywords: ['offline', 'no internet', 'works offline', 'offline mode', 'no connection', 'offline scanning'],
    question: 'Does the app work offline?',
    answer:
      'Partial offline support is available:\n• **Staff Scanner** — Can validate tickets offline by caching ticket data before the event\n• **My Tickets** — Previously loaded tickets are cached and viewable offline\n• **QR Codes** — Your ticket QR code is stored locally\n\n**Requires internet:**\n• Purchasing tickets\n• Marketplace transactions\n• Swapping tokens\n• Creating support tickets\n\nThe staff scanner is designed specifically for events where internet may be unreliable.',
    category: 'technical',
  },

  // ═══════════════════════════════════════════
  // ─── POS ADVANCED ─────────────────────────
  // ═══════════════════════════════════════════
  {
    id: 'pos-products',
    keywords: ['pos products', 'add products', 'product catalog', 'menu items', 'pos inventory', 'product management'],
    question: 'How do I manage products on the POS?',
    answer:
      'To manage your POS product catalog:\n1. Open the **POS Dashboard**\n2. Go to **Products** or **Quick Products**\n3. **Add Product** — Enter name, price (in PGT), category, and optional image\n4. **Edit** — Update prices or details anytime\n5. **Categories** — Organize products into groups (food, drinks, merch, etc.)\n\nQuick Products appear as one-tap buttons on the POS terminal for fast checkout.',
    category: 'event_inquiry',
  },
  {
    id: 'pos-multi-terminal',
    keywords: ['multiple terminals', 'multi terminal', 'second terminal', 'pos on multiple devices', 'terminal registration'],
    question: 'Can I use multiple POS terminals?',
    answer:
      'Yes! Multiple POS terminals can be linked to one merchant account:\n1. Open the POS app on a new device\n2. Go to **Settings** → **Terminal Registration**\n3. Enter your merchant wallet address\n4. Each terminal gets a unique ID for tracking\n\nAll terminals share the same product catalog and sales data. The merchant dashboard shows per-terminal and aggregate statistics.',
    category: 'event_inquiry',
  },
  {
    id: 'pos-receipt',
    keywords: ['receipt', 'pos receipt', 'transaction receipt', 'proof of purchase', 'digital receipt'],
    question: 'How do POS receipts work?',
    answer:
      'After each POS transaction:\n• **Digital receipt** — Automatically generated with transaction details\n• **On-chain proof** — Transaction hash links to BaseScan for verification\n• **IPFS storage** — Receipts are optionally stored on IPFS for permanence\n• **QR code** — Receipt includes a QR linking to the transaction\n\nCustomers and merchants can view receipts from the transaction history.',
    category: 'event_inquiry',
  },
  {
    id: 'pos-coupon-apply',
    keywords: ['pos coupon', 'apply coupon pos', 'discount at pos', 'coupon at terminal', 'pos discount'],
    question: 'How do I apply coupons at POS?',
    answer:
      'To apply a coupon during a POS transaction:\n1. Ring up the customer\'s items\n2. Tap **Apply Coupon** before checkout\n3. Enter the coupon code or scan the customer\'s coupon QR\n4. The discount is applied to the total\n5. Complete the PGT payment\n\nCoupon validity is checked on-chain in real-time. Invalid or expired coupons will be rejected automatically.',
    category: 'event_inquiry',
  },

  // ═══════════════════════════════════════════
  // ─── MERCHANT ADMIN PORTAL ────────────────
  // ═══════════════════════════════════════════
  {
    id: 'merchant-admin',
    keywords: ['merchant admin', 'merchant portal', 'merchant dashboard', 'vendor dashboard', 'manage merchant'],
    question: 'What is the Merchant Admin portal?',
    answer:
      'The Merchant Admin portal is for managing merchant operations:\n• **Dashboard** — Overview of all merchant metrics\n• **License Management** — View, purchase, and manage module licenses\n• **POS Management** — Configure terminals, products, settings\n• **Fee Configuration** — Set transaction fee structures\n• **Coupon Management** — Create and manage coupon campaigns\n• **Loyalty Configuration** — Set up loyalty rewards programs\n• **Reports** — View coupon redemptions, sales analytics\n\nAccess requires a merchant license and admin wallet.',
    category: 'other',
  },

  // ═══════════════════════════════════════════
  // ─── SUPER ADMIN ──────────────────────────
  // ═══════════════════════════════════════════
  {
    id: 'super-admin',
    keywords: ['super admin', 'superadmin', 'system admin', 'global admin', 'ecosystem management'],
    question: 'What can the Super Admin do?',
    answer:
      'The Super Admin has system-wide control:\n• **Ecosystem Management** — Create and manage multiple ecosystems\n• **Global Analytics** — System-wide statistics and health monitoring\n• **Module Store** — Purchase and distribute feature modules\n• **Configuration** — Toggle features, set system parameters\n• **License Control** — Grant, revoke, or adjust licenses\n• **Settings** — Global system settings\n\nSuper Admin access is restricted to the designated system wallet.',
    category: 'other',
  },
  {
    id: 'module-store',
    keywords: ['module store', 'buy module', 'feature modules', 'unlock features', 'module marketplace'],
    question: 'What is the Module Store?',
    answer:
      'The Module Store lets you purchase optional feature modules for your ecosystem:\n• **Support Module** — Ticket system + chat widget\n• **Coupon Module** — On-chain discount codes\n• **Loyalty Module** — Points and tier rewards\n• **POS Module** — Point of sale terminals\n• **Analytics Module** — Advanced reporting\n\nModules are purchased with PGT tokens. Each module activates immediately and is tied to your merchant license. Pricing varies — check the Module Store for current rates.',
    category: 'other',
  },

  // ═══════════════════════════════════════════
  // ─── LICENSING DETAILS ────────────────────
  // ═══════════════════════════════════════════
  {
    id: 'license-types',
    keywords: ['license types', 'license tiers', 'license pricing', 'which license', 'license comparison'],
    question: 'What license types are available?',
    answer:
      'License tiers available:\n• **Basic** — Core functionality (ticketing, marketplace)\n• **Merchant** — POS system, product management, receipts\n• **Enterprise** — All features + advanced analytics, multi-terminal, custom branding\n\nAdditional module licenses can be purchased separately:\n• Support Ticketing\n• Coupon System\n• Loyalty Program\n• DEX Liquidity\n\nLicenses are on-chain NFTs managed by the License Manager smart contract.',
    category: 'other',
  },
  {
    id: 'license-expired',
    keywords: ['license expired', 'license renewal', 'renew license', 'license inactive', 'license not working'],
    question: 'My license expired',
    answer:
      'If your license has expired:\n1. **Check status** — Go to your License panel to see expiry date\n2. **Renew** — Click "Renew" and pay the renewal fee in PGT\n3. **Grace period** — Some licenses have a short grace period after expiry\n4. **Features locked** — Expired licenses disable associated features until renewed\n\nContact the super admin if you believe your license should still be active.',
    category: 'other',
  },

  // ═══════════════════════════════════════════
  // ─── DATA EXPORT ──────────────────────────
  // ═══════════════════════════════════════════
  {
    id: 'csv-export',
    keywords: ['export', 'csv', 'download data', 'export data', 'spreadsheet', 'excel', 'export report'],
    question: 'How do I export data?',
    answer:
      'Data can be exported from several places:\n• **Admin Panel** — Export ticket sales, user data, event reports as CSV\n• **Staff Scanner** — Export scan/check-in logs as CSV\n• **Merchant Dashboard** — Export POS transaction data\n• **Analytics** — Download report data as spreadsheets\n\nCSV files can be opened in Excel, Google Sheets, or any spreadsheet application.',
    category: 'other',
  },

  // ═══════════════════════════════════════════
  // ─── LANGUAGE & SETTINGS ──────────────────
  // ═══════════════════════════════════════════
  {
    id: 'language',
    keywords: ['language', 'change language', 'hebrew', 'english', 'russian', 'translation', 'multi language'],
    question: 'Can I change the app language?',
    answer:
      'On the mobile app:\n1. Go to **Settings** → **Language**\n2. Select your preferred language\n3. The app will update immediately\n\nThe web app currently defaults to English. Language support may vary by feature. If you need help in another language, mention it in your support ticket.',
    category: 'technical',
  },
  {
    id: 'currency-display',
    keywords: ['currency', 'usd', 'eur', 'display currency', 'fiat currency', 'dollar', 'show price in'],
    question: 'Can I see prices in my local currency?',
    answer:
      'Yes! The mobile app supports currency display preferences:\n1. Go to **Settings** → **Currency**\n2. Select your preferred currency (USD, EUR, ILS, etc.)\n3. Prices will show approximate fiat values alongside PGT amounts\n\nNote: Fiat values are estimates based on current PGT/ETH exchange rates. All actual transactions are in PGT tokens.',
    category: 'technical',
  },

  // ═══════════════════════════════════════════
  // ─── PRIVACY & TERMS ──────────────────────
  // ═══════════════════════════════════════════
  {
    id: 'privacy',
    keywords: ['privacy', 'data privacy', 'personal data', 'data collection', 'privacy policy', 'gdpr'],
    question: 'How is my data protected?',
    answer:
      'We prioritize your privacy:\n• **Wallet-based auth** — No personal data or passwords required to use the platform\n• **On-chain data** — Transactions are on the public blockchain (wallet addresses, not names)\n• **Minimal data** — We only store what\'s needed for support tickets (if you provide email, it\'s optional)\n• **No tracking** — We don\'t sell or share your data\n\nView the full privacy policy in Settings → Privacy Policy.',
    category: 'other',
  },
  {
    id: 'terms',
    keywords: ['terms', 'terms of service', 'tos', 'rules', 'terms and conditions', 'user agreement'],
    question: 'Where are the terms of service?',
    answer:
      'You can find the Terms of Service:\n• **Mobile app** — Settings → Terms of Service\n• **Web app** — Footer link or About section\n\nKey points:\n• You\'re responsible for your wallet security\n• Ticket purchases are subject to event organizer policies\n• Marketplace sales are final once confirmed on-chain\n• We reserve the right to suspend accounts that violate platform rules',
    category: 'other',
  },

  // ═══════════════════════════════════════════
  // ─── TOKEN VESTING & ICO ──────────────────
  // ═══════════════════════════════════════════
  {
    id: 'token-vesting',
    keywords: ['vesting', 'token vesting', 'locked tokens', 'vesting schedule', 'token release'],
    question: 'What is token vesting?',
    answer:
      'Token vesting locks PGT tokens and releases them gradually over time:\n• **Purpose** — Ensures long-term commitment from team and early investors\n• **Schedule** — Tokens unlock on a defined schedule (e.g., monthly over 12 months)\n• **Cliff** — An initial waiting period before any tokens unlock\n• **Claim** — Once tokens are unlocked, holders can claim them to their wallet\n\nVesting details for your allocation (if applicable) are visible in the Vesting dashboard.',
    category: 'account',
  },
  {
    id: 'ico',
    keywords: ['ico', 'token sale', 'buy pgt ico', 'initial offering', 'presale', 'token launch'],
    question: 'Is there a PGT token sale?',
    answer:
      'PGT token sales may be available periodically:\n• **ICO (Initial Coin Offering)** — Early access to PGT at discounted rates\n• **Presale** — Limited quantity at lower prices for early supporters\n• **Public sale** — Available to all users\n\nCheck the ICO page (if available) or follow official announcements for upcoming token sale events. After the sale, PGT can always be acquired through the swap feature.',
    category: 'account',
  },

  // ═══════════════════════════════════════════
  // ─── BATCH OPERATIONS ─────────────────────
  // ═══════════════════════════════════════════
  {
    id: 'batch-purchase',
    keywords: ['batch purchase', 'buy many tickets', 'group purchase', 'batch mint', 'buy for friends', 'bulk tickets'],
    question: 'How do I buy tickets in batch?',
    answer:
      'To buy multiple tickets at once:\n1. On the event page, select the ticket type\n2. Increase the quantity (up to the per-wallet limit)\n3. Click **Buy** — all tickets are minted in a single transaction\n\nBenefits of batch purchasing:\n• **Cheaper gas** — One transaction for multiple tickets\n• **Bonus XP** — Earn 20 XP per ticket for batches of 3+\n• **Group convenience** — Buy for your whole group, then transfer individual tickets\n\nAfter purchase, each ticket is a separate NFT you can transfer individually.',
    category: 'tickets',
  },

  // ═══════════════════════════════════════════
  // ─── IMAGE & MEDIA ────────────────────────
  // ═══════════════════════════════════════════
  {
    id: 'event-images',
    keywords: ['event image', 'upload image', 'event photo', 'event banner', 'event media', 'nft image'],
    question: 'How are event images managed?',
    answer:
      'Event images are managed by organizers:\n• **Event banner** — Main image displayed on the event page and slider\n• **Ticket artwork** — Custom NFT artwork for each ticket type\n• **Upload** — Admins upload images via the Event Form in the Admin panel\n• **Storage** — Images are stored and served via the media service\n• **NFT metadata** — Ticket images are referenced in the on-chain NFT metadata\n\nSupported formats: JPG, PNG, WebP. Recommended resolution: 1200x630px for banners.',
    category: 'event_inquiry',
  },

  // ═══════════════════════════════════════════
  // ─── LOYALTY POS INTEGRATION ──────────────
  // ═══════════════════════════════════════════
  {
    id: 'pos-loyalty',
    keywords: ['pos loyalty', 'loyalty at pos', 'earn points pos', 'loyalty terminal', 'customer loyalty pos'],
    question: 'How does loyalty work at POS?',
    answer:
      'Loyalty rewards integrate with the POS system:\n• **Earn points** — Customers automatically earn loyalty points on POS purchases\n• **Redeem** — Points can be redeemed for discounts at the terminal\n• **Tier display** — The POS shows the customer\'s loyalty tier when they connect\n• **Streak bonuses** — Regular customers get multiplied points\n\nMerchants can configure loyalty rules from the Merchant Admin → Loyalty Configuration.',
    category: 'event_inquiry',
  },

  // ═══════════════════════════════════════════
  // ─── FORECASTING ──────────────────────────
  // ═══════════════════════════════════════════
  {
    id: 'forecasting',
    keywords: ['forecast', 'sales forecast', 'predict sales', 'forecasting', 'future sales', 'projections'],
    question: 'What is the sales forecasting feature?',
    answer:
      'Sales forecasting helps event organizers plan:\n• **Predicted sales** — ML-based predictions of ticket sales over time\n• **Revenue projection** — Estimated total revenue for upcoming events\n• **Trend analysis** — Historical sales patterns and seasonality\n• **Capacity planning** — Recommendations for ticket quantities\n\nAccess forecasting from the **Admin** → **Analytics** → **Forecasting** section. Accuracy improves with more historical data.',
    category: 'other',
  },

  // ═══════════════════════════════════════════════════
  // ─── USER JOURNEY: GETTING STARTED ────────────────
  // ═══════════════════════════════════════════════════
  {
    id: 'getting-started',
    keywords: ['getting started', 'how to start', 'first time', 'new user', 'beginner', 'signup', 'sign up', 'register', 'onboarding'],
    question: 'How do I get started?',
    answer:
      'Welcome! Here\'s your complete getting-started guide:\n\n**Step 1 — Get a Wallet**\nInstall MetaMask (browser extension) or download our mobile app.\n\n**Step 2 — Connect**\nClick "MetaMask", "WalletConnect", or "Coinbase Wallet" to connect.\n\n**Step 3 — Get on Base Network**\nThe app auto-prompts you to switch. If not: MetaMask → Networks → Base.\n\n**Step 4 — Get ETH for Gas**\nYou need a tiny amount of ETH on Base (<$0.01 per transaction). Bridge from Ethereum or buy on an exchange.\n\n**Step 5 — Get PGT Tokens**\nSwap ETH→PGT using the built-in swap, or use "Buy PGT".\n\n**Step 6 — Buy Your First Ticket!**\nBrowse events, pick a ticket, click Buy, and approve.\n\nThat\'s it — your NFT ticket appears in "My Tickets"!',
    category: 'account',
  },
  {
    id: 'first-event',
    keywords: ['first event', 'attend event', 'go to event', 'event day', 'day of event', 'what to do at event'],
    question: 'What do I do on event day?',
    answer:
      'Event day checklist:\n\n**Before you leave:**\n1. Make sure your wallet is connected and your ticket is visible in My Tickets\n2. Charge your phone — you\'ll need it for the QR code\n3. Have internet access (mobile data or WiFi)\n\n**At the venue:**\n1. Open the app → My Tickets\n2. Select your ticket for this event\n3. Show the QR code to the staff scanner\n4. Green = you\'re in!\n\n**At the event:**\n• Use PGT to buy food/drinks at POS terminals\n• Earn XP for checking in\n• Check loyalty rewards\n\n**After the event:**\nYour ticket NFT stays in your wallet as a collectible!',
    category: 'event_inquiry',
  },

  // ═══════════════════════════════════════════════════
  // ─── USER JOURNEY: WALLET ADDRESS ─────────────────
  // ═══════════════════════════════════════════════════
  {
    id: 'find-my-address',
    keywords: ['my address', 'wallet address', 'find my address', 'where is my address', 'what is my address', 'copy address', 'share address'],
    question: 'Where do I find my wallet address?',
    answer:
      'Your wallet address (0x...) can be found in several places:\n\n**Web App:**\n• Top of the page when connected — shows shortened address (0x1234...5678)\n• Click it to copy the full address\n\n**MetaMask:**\n• Click your account name at the top to copy\n\n**Mobile App:**\n• Wallet tab → Receive → shows address + QR code\n• Tap the address to copy it\n\nYour address is public and safe to share. It\'s like your bank account number — people need it to send you tokens, but they can\'t take anything from it.',
    category: 'account',
  },
  {
    id: 'share-address-qr',
    keywords: ['share address qr', 'qr for address', 'show my qr', 'receive qr', 'payment qr'],
    question: 'How do I share my address via QR?',
    answer:
      'To share your wallet address as a QR code:\n\n**Mobile App:**\n1. Go to **Wallet** → **Receive**\n2. Your QR code and address are displayed\n3. Let the sender scan it, or tap **Share** to send via message\n\n**Web App:**\n• Click your address at the top → copy it and share manually\n\n**POS (merchant):**\n• The POS terminal generates payment QR codes automatically for each transaction',
    category: 'account',
  },

  // ═══════════════════════════════════════════════════
  // ─── USER JOURNEY: PAYING WITH PGT ────────────────
  // ═══════════════════════════════════════════════════
  {
    id: 'pay-with-pgt',
    keywords: ['pay with pgt', 'how to pay', 'use pgt', 'payment', 'pgt payment', 'pay for ticket', 'pay at event'],
    question: 'How do I pay with PGT?',
    answer:
      'PGT is used for all payments in the ecosystem:\n\n**Buying tickets:**\n1. Select event → choose ticket type → click Buy\n2. Your wallet asks to approve a PGT transfer\n3. Click Confirm → ticket is minted to your wallet\n\n**Marketplace:**\n1. Find a listing → click Buy\n2. Approve PGT spend → confirm transaction\n\n**At POS terminals (food/drinks/merch):**\n1. Merchant shows you a payment QR code\n2. Scan it with your wallet app\n3. Approve the PGT amount → done!\n\nAll PGT payments are on-chain transactions on Base.',
    category: 'account',
  },
  {
    id: 'approve-pgt-spend',
    keywords: ['approve', 'allowance', 'approve spending', 'token approval', 'approve pgt', 'spending limit', 'approve contract'],
    question: 'What is "Approve" when buying?',
    answer:
      'Before your first purchase, you need to **approve** the smart contract to spend your PGT:\n\n1. **First time only** — You\'ll see an "Approve" transaction before the actual purchase\n2. **What it does** — Gives the contract permission to transfer PGT on your behalf\n3. **Safe** — Only the specific contract can use the approval, and only for the approved amount\n4. **One-time** — After approval, future purchases go through directly\n\nThis is a standard ERC-20 pattern. You may see two transactions on your first buy: approve + purchase.',
    category: 'technical',
  },

  // ═══════════════════════════════════════════════════
  // ─── USER JOURNEY: REQUESTING PAYMENT ─────────────
  // ═══════════════════════════════════════════════════
  {
    id: 'request-payment',
    keywords: ['request payment', 'invoice', 'payment request', 'ask for payment', 'send invoice', 'charge customer'],
    question: 'How do I request a payment?',
    answer:
      'There are several ways to request PGT payment:\n\n**As a Merchant (POS):**\n1. Open POS terminal → add items or enter amount\n2. Click **Request Payment** — a QR code is generated\n3. Customer scans the QR and approves\n4. Payment confirmed instantly on-chain\n\n**As a User (peer-to-peer):**\n1. Go to Wallet → **Receive**\n2. Share your address or QR code\n3. Tell the sender the PGT amount\n4. They send it to your address\n\n**Tip:** For POS payments, the terminal tracks everything automatically.',
    category: 'account',
  },

  // ═══════════════════════════════════════════════════
  // ─── USER JOURNEY: ETH ON BASE ────────────────────
  // ═══════════════════════════════════════════════════
  {
    id: 'get-eth-base',
    keywords: ['get eth', 'eth on base', 'bridge eth', 'need eth', 'no eth', 'fund wallet', 'how to get eth', 'base bridge'],
    question: 'How do I get ETH on Base for gas?',
    answer:
      'You need a small amount of ETH on Base for gas fees (~$0.01 per tx):\n\n**Option 1 — Bridge from Ethereum:**\n1. Go to bridge.base.org\n2. Connect your wallet\n3. Bridge ETH from Ethereum mainnet to Base\n\n**Option 2 — Buy on an exchange:**\n1. Buy ETH on Coinbase, Binance, etc.\n2. Withdraw directly to Base network (if supported)\n\n**Option 3 — Coinbase Wallet:**\n• Coinbase Wallet has built-in Base support\n• Buy ETH directly in the app\n\n**Option 4 — Gasless mode:**\n• Some transactions are gas-sponsored (no ETH needed)\n• The PGT Paymaster can cover gas using your PGT tokens',
    category: 'account',
  },

  // ═══════════════════════════════════════════════════
  // ─── USER JOURNEY: FULL BUY PGT FLOW ──────────────
  // ═══════════════════════════════════════════════════
  {
    id: 'buy-pgt-step-by-step',
    keywords: ['buy pgt step', 'buy pgt detailed', 'pgt purchase flow', 'swap eth pgt', 'buy pgt button'],
    question: 'Walk me through buying PGT step by step',
    answer:
      'Complete PGT purchase flow:\n\n**Method 1 — Built-in Swap:**\n1. Make sure you have ETH on Base\n2. Click the **Swap** button (or "Buy PGT")\n3. Enter the ETH amount you want to swap\n4. Review the PGT amount you\'ll receive + exchange rate\n5. Click **Swap** → confirm in MetaMask\n6. PGT appears in your balance within seconds\n\n**Method 2 — Direct Buy (if available):**\n1. Click **Buy PGT** on the home page\n2. Choose an amount\n3. Pay with ETH → receive PGT\n\n**Method 3 — Native Currency Swap:**\n• On supported chains, swap the native currency directly for PGT\n\nYou can see your PGT balance at the top of the page.',
    category: 'account',
  },

  // ═══════════════════════════════════════════════════
  // ─── USER JOURNEY: COMPLETE TICKET FLOW ───────────
  // ═══════════════════════════════════════════════════
  {
    id: 'ticket-lifecycle',
    keywords: ['ticket lifecycle', 'ticket flow', 'ticket journey', 'from purchase to event', 'ticket start to finish'],
    question: 'What is the full ticket lifecycle?',
    answer:
      'A ticket goes through these stages:\n\n1. **Purchase** — Buy with PGT → NFT minted to your wallet\n2. **Confirmation** — Transaction confirmed on-chain (~2 sec)\n3. **My Tickets** — Appears in your collection with QR code\n4. **Optional: List** — List on marketplace for resale\n5. **Optional: Transfer** — Send to a friend\'s wallet\n6. **Event Day** — Show QR at entrance → staff scans\n7. **Check-in** — QR validated, ticket marked as used\n8. **Collectible** — NFT stays in your wallet as proof of attendance\n\nYou earn XP at steps 1 and 7!',
    category: 'tickets',
  },

  // ═══════════════════════════════════════════════════
  // ─── USER JOURNEY: EIP-712 SIGNATURES ─────────────
  // ═══════════════════════════════════════════════════
  {
    id: 'signature-request',
    keywords: ['signature request', 'sign message', 'metamask popup', 'approve signature', 'eip 712', 'what am i signing'],
    question: 'Why does my wallet ask me to sign a message?',
    answer:
      'Signature requests are used for gasless authentication:\n\n• **Buying tickets** — The relayer system uses EIP-712 signatures so you don\'t pay gas\n• **Admin actions** — Admin operations are verified via signatures\n• **QR codes** — Signed to prevent forgery\n\n**What you see:** A MetaMask popup showing structured data to sign\n**Is it safe?** Yes — signing a message does NOT transfer any tokens. It\'s like signing a document, not making a payment.\n\n**Red flags:** If a random site asks you to sign, check the domain. Only sign on the official app.',
    category: 'technical',
  },

  // ═══════════════════════════════════════════════════
  // ─── GAMIFICATION DETAILS ─────────────────────────
  // ═══════════════════════════════════════════════════
  {
    id: 'xp-levels',
    keywords: ['xp levels', 'what level', 'level names', 'xp tiers', 'level progression', 'hall of fame'],
    question: 'What are the XP levels?',
    answer:
      'The XP system has 10 levels:\n\n| Level | Title | XP Required |\n|-------|-------|-------------|\n| 1 | Newcomer | 0 |\n| 2 | Fan | 100 |\n| 3 | Regular | 300 |\n| 4 | Enthusiast | 600 |\n| 5 | VIP | 1,000 |\n| 6 | Legend | 1,500 |\n| 7 | Master | 2,500 |\n| 8 | Champion | 4,000 |\n| 9 | Elite | 6,000 |\n| 10 | Hall of Fame | 10,000 |\n\nCheck your progress in the **XP & Levels** tab.',
    category: 'event_inquiry',
  },
  {
    id: 'xp-actions-full',
    keywords: ['xp actions', 'how to earn xp', 'all xp rewards', 'xp points list', 'earn more xp'],
    question: 'What are all the ways to earn XP?',
    answer:
      'Complete XP reward list:\n\n• **Buy a ticket** — 50 XP\n• **Check in at event** — 100 XP\n• **First purchase bonus** — 100 XP (one-time)\n• **Referral** — 150 XP per referral\n• **Loyalty tier up** — 200 XP\n• **Early bird purchase** (7+ days early) — 75 XP\n• **Batch purchase** (3+ tickets) — 20 XP per ticket\n• **Profile complete** — 50 XP\n• **Connect wallet** — 25 XP (one-time)\n• **Claim a perk** — 25 XP\n• **Use a coupon** — 15 XP\n• **Daily login** — 5 XP\n• **7-day streak** — 100 XP bonus\n• **30-day streak** — 500 XP bonus',
    category: 'event_inquiry',
  },
  {
    id: 'daily-login',
    keywords: ['daily login', 'login streak', 'daily reward', 'streak bonus', 'consecutive login', 'daily xp'],
    question: 'How do daily login streaks work?',
    answer:
      'Earn XP just by logging in:\n• **Daily login** — 5 XP every day you open the app\n• **7-day streak** — 100 XP bonus for 7 consecutive days\n• **30-day streak** — 500 XP bonus for 30 consecutive days\n\nStreaks reset if you miss a day. The streak counter is visible in the XP & Levels tab.',
    category: 'event_inquiry',
  },

  // ═══════════════════════════════════════════════════
  // ─── AMBASSADOR TIERS DETAIL ──────────────────────
  // ═══════════════════════════════════════════════════
  {
    id: 'ambassador-tiers',
    keywords: ['ambassador tier', 'ambassador levels', 'ambassador commission', 'vip ambassador', 'ambassador requirements'],
    question: 'What are the Ambassador tier levels?',
    answer:
      'The Ambassador program has 3 tiers:\n\n**Standard (starting tier):**\n• 5% commission on referral ticket sales\n• Basic referral link\n\n**Ambassador (10+ referrals, 500+ PGT monthly volume):**\n• 10% commission\n• Priority support\n• Early access to new events\n\n**VIP (50+ referrals, 2,500+ PGT monthly volume):**\n• 15% commission\n• Exclusive events access\n• Custom promotional materials\n• Direct contact with the team\n\nYour tier is recalculated monthly based on active referrals and volume.',
    category: 'event_inquiry',
  },
  {
    id: 'ambassador-commission',
    keywords: ['commission', 'referral earnings', 'how much commission', 'commission rate', 'claim commission'],
    question: 'How do Ambassador commissions work?',
    answer:
      'Commissions are earned when your referrals make purchases:\n\n1. **Earn** — When someone uses your referral link and buys a ticket, you earn a % of the sale\n2. **Track** — View earnings in your Ambassador Dashboard → Commission History\n3. **Claim** — Accumulated commissions can be claimed to your wallet\n4. **Rate** — Depends on your tier: 5% (Standard), 10% (Ambassador), 15% (VIP)\n\nCommissions are calculated on the PGT value of each ticket sale. Volume is tracked monthly for tier progression.',
    category: 'event_inquiry',
  },
  {
    id: 'leaderboard',
    keywords: ['leaderboard', 'top referrers', 'ranking', 'ambassador ranking', 'top ambassadors'],
    question: 'How does the Ambassador leaderboard work?',
    answer:
      'The leaderboard ranks ambassadors by performance:\n• **Ranking criteria** — Total referrals, monthly volume, commission earned\n• **Visibility** — See your rank vs. other ambassadors\n• **Rewards** — Top performers may receive bonus rewards\n• **Monthly reset** — Volume-based rankings refresh each month\n\nAccess the leaderboard from the Ambassador Dashboard on mobile or the Referral section on web.',
    category: 'event_inquiry',
  },

  // ═══════════════════════════════════════════════════
  // ─── COUPON SYSTEM DETAILS ────────────────────────
  // ═══════════════════════════════════════════════════
  {
    id: 'coupon-types',
    keywords: ['coupon types', 'types of coupons', 'discount types', 'percentage coupon', 'fixed discount'],
    question: 'What types of coupons are available?',
    answer:
      'The platform supports several on-chain coupon types:\n\n• **Percentage discount** — e.g., 20% off the ticket price\n• **Fixed amount** — e.g., 50 PGT off\n• **Buy X Get Y** — Bundle deals (e.g., buy 3 get 1 free)\n• **Fee waiver** — Waive platform/service fees\n\n**Restrictions can include:**\n• First-time buyers only\n• Specific events only\n• Minimum spend required\n• Limited total uses\n• Expiry date\n\nAll coupons are verified on-chain, so they can\'t be faked or double-used.',
    category: 'tickets',
  },
  {
    id: 'coupon-create',
    keywords: ['create coupon', 'make coupon', 'issue coupon', 'admin coupon', 'set up coupon'],
    question: 'How do I create a coupon? (admin/merchant)',
    answer:
      'To create a coupon (requires merchant or admin access):\n1. Go to **Merchant Admin** → **Coupon Management** (or Admin → Coupons)\n2. Click **Create Coupon**\n3. Configure:\n   • Coupon code (or auto-generate)\n   • Discount type (percentage, fixed, bundle)\n   • Discount amount\n   • Restrictions (events, min spend, first-time only)\n   • Usage limit and expiry date\n4. Deploy — the coupon is created on-chain\n\nCoupons are managed via the PGTCouponSystem smart contract.',
    category: 'other',
  },

  // ═══════════════════════════════════════════════════
  // ─── LOYALTY PROGRAM DETAILS ──────────────────────
  // ═══════════════════════════════════════════════════
  {
    id: 'loyalty-reward-types',
    keywords: ['loyalty reward types', 'how loyalty works', 'loyalty rules', 'earn loyalty', 'staking rewards'],
    question: 'What types of loyalty rewards are there?',
    answer:
      'The loyalty program has multiple reward types:\n\n• **Spend Amount** — Earn rewards based on total PGT spent\n• **Transaction Count** — Bonus per number of purchases\n• **Event Attendance** — Rewards for attending events\n• **Referral** — Bonus for referring new users\n• **Streak** — Consecutive activity rewards (daily/weekly)\n• **Volume Threshold** — One-time milestone bonuses\n• **Staking** — Earn rewards by holding/staking PGT tokens\n\nReward rules are set by the organizer/merchant via the PGTLoyaltyProgram smart contract.',
    category: 'event_inquiry',
  },
  {
    id: 'loyalty-diamond',
    keywords: ['diamond tier', 'highest tier', 'max loyalty', 'loyalty diamond', 'all loyalty tiers'],
    question: 'What are all the loyalty tiers?',
    answer:
      'The loyalty program has 5 tiers:\n\n• **Bronze** — Starting tier, basic rewards\n• **Silver** — After 3 purchases or 500 PGT spent\n• **Gold** — After 10 purchases or 2,000 PGT spent\n• **Platinum** — After 25 purchases or 5,000 PGT spent\n• **Diamond** — After 50 purchases or 10,000 PGT spent\n\nHigher tiers unlock:\n• Better discount rates\n• Exclusive event access\n• Priority support\n• Higher loyalty point multipliers\n• VIP perks at events',
    category: 'event_inquiry',
  },
  {
    id: 'staking',
    keywords: ['staking', 'stake pgt', 'hold pgt', 'staking rewards', 'passive income', 'lock tokens'],
    question: 'Can I stake PGT for rewards?',
    answer:
      'The loyalty program supports staking rewards:\n• **How it works** — Hold PGT in your wallet to earn periodic loyalty rewards\n• **No lock-up** — Unlike traditional staking, your tokens remain in your wallet\n• **Automatic** — Rewards accrue based on your balance and the loyalty rules set by the organizer\n• **Claim** — Accumulated rewards can be claimed through the loyalty dashboard\n\nStaking reward rates vary by ecosystem. Check the Loyalty section for current rates.',
    category: 'account',
  },

  // ═══════════════════════════════════════════════════
  // ─── QR CODE TIMING & SECURITY ────────────────────
  // ═══════════════════════════════════════════════════
  {
    id: 'qr-refresh',
    keywords: ['qr refresh', 'qr expires', 'qr timeout', 'qr code time', 'new qr code', 'qr 60 seconds'],
    question: 'Why does my QR code keep refreshing?',
    answer:
      'QR codes refresh every 60 seconds for security:\n• **Time-limited** — Each QR is valid for ~60 seconds\n• **Server-signed** — Every QR contains a cryptographic signature from the server\n• **Anti-screenshot** — Expired QRs can\'t be reused, so screenshots won\'t work\n• **Auto-refresh** — The app automatically generates a new QR when the old one expires\n\nThis prevents ticket fraud — even if someone screenshots your QR, it expires quickly.',
    category: 'event_inquiry',
  },

  // ═══════════════════════════════════════════════════
  // ─── FEE STRUCTURE ────────────────────────────────
  // ═══════════════════════════════════════════════════
  {
    id: 'fee-structure',
    keywords: ['fees', 'fee structure', 'platform fees', 'how much are fees', 'service fee', 'fee breakdown'],
    question: 'What fees does the platform charge?',
    answer:
      'Fee overview:\n\n**Ticket Purchase:**\n• Gas fee: ~$0.01 (ETH on Base)\n• Platform fee: Set by organizer (typically 2-5%)\n\n**Marketplace (resale):**\n• Seller fee: Small platform percentage\n• Buyer: Pays listed price + gas only\n\n**Token Swap:**\n• Swap fee: Small percentage\n• Gas: ~$0.01\n\n**POS Payments:**\n• Transaction fee: Set by merchant/admin\n• Gas: ~$0.01\n\nExact fee percentages are shown before you confirm any transaction. Admins can configure fee structures from the Admin panel.',
    category: 'technical',
  },

  // ═══════════════════════════════════════════════════
  // ─── BIOMETRIC AUTH ───────────────────────────────
  // ═══════════════════════════════════════════════════
  {
    id: 'biometrics',
    keywords: ['biometric', 'face id', 'fingerprint', 'touch id', 'biometric login', 'face recognition'],
    question: 'Can I use Face ID / fingerprint?',
    answer:
      'Yes, on the mobile app:\n1. Go to **Settings** → **Security**\n2. Enable **Biometric Authentication**\n3. Confirm with your PIN\n4. Now you can unlock the app with Face ID or fingerprint\n\n**Note:** Biometrics are an additional convenience layer. Your PIN is always available as a fallback. High-value operations (like exporting your recovery phrase) still require your PIN.',
    category: 'account',
  },

  // ═══════════════════════════════════════════════════
  // ─── NFT METADATA & BASESCAN ──────────────────────
  // ═══════════════════════════════════════════════════
  {
    id: 'verify-on-basescan',
    keywords: ['basescan', 'verify transaction', 'check transaction', 'transaction hash', 'tx hash', 'block explorer'],
    question: 'How do I verify a transaction on BaseScan?',
    answer:
      'To check any transaction on BaseScan:\n1. Go to **basescan.org**\n2. Paste your **wallet address** to see all transactions\n3. Or paste a **transaction hash** (0x...) for a specific transaction\n\n**What you can see:**\n• Status (Success / Failed)\n• Gas used and cost\n• Tokens transferred\n• Smart contract interactions\n• Timestamp and block number\n\nEvery ticket purchase, transfer, and marketplace sale is visible on BaseScan.',
    category: 'technical',
  },
  {
    id: 'nft-metadata',
    keywords: ['nft metadata', 'ticket metadata', 'token uri', 'nft details', 'on chain data', 'ipfs metadata'],
    question: 'What data is stored in my ticket NFT?',
    answer:
      'Each ticket NFT contains metadata:\n• **Event name** — Which event the ticket is for\n• **Ticket type** — GA, VIP, Early Bird, etc.\n• **Token ID** — Unique identifier on-chain\n• **Image** — Event artwork or ticket design\n• **Event date** — When the event takes place\n• **Venue** — Event location\n• **Check-in status** — Whether the ticket has been used\n\nMetadata is stored on IPFS for permanence and referenced by the smart contract.',
    category: 'tickets',
  },

  // ═══════════════════════════════════════════════════
  // ─── MARKETPLACE COLLECTIONS ──────────────────────
  // ═══════════════════════════════════════════════════
  {
    id: 'marketplace-collections',
    keywords: ['collections', 'nft collections', 'browse collections', 'event collection', 'all collections'],
    question: 'What are marketplace collections?',
    answer:
      'Collections group tickets by event:\n• **Browse** — The Collections page shows all events with marketplace activity\n• **Filter** — View only tickets from specific events\n• **Stats** — See floor price, total volume, and listed count per collection\n• **Active/Past** — Filter between upcoming and past events\n\nEach event\'s tickets form a collection. Past-event tickets may still have collectible value.',
    category: 'tickets',
  },

  // ═══════════════════════════════════════════════════
  // ─── MOBILE SWAP ──────────────────────────────────
  // ═══════════════════════════════════════════════════
  {
    id: 'mobile-swap',
    keywords: ['mobile swap', 'swap on phone', 'mobile exchange', 'swap app'],
    question: 'How do I swap tokens on mobile?',
    answer:
      'On the mobile app:\n1. Go to the **Swap** screen (usually in Wallet or main navigation)\n2. Select the tokens (e.g., ETH → PGT)\n3. Enter the amount\n4. Review the exchange rate and estimated output\n5. Tap **Swap** → approve with your PIN or biometrics\n6. Wait for confirmation (~2 seconds)\n\nThe mobile swap uses the same on-chain liquidity pool as the web app.',
    category: 'account',
  },

  // ═══════════════════════════════════════════════════
  // ─── REFUND PROCESS DETAIL ────────────────────────
  // ═══════════════════════════════════════════════════
  {
    id: 'refund-process',
    keywords: ['refund process', 'how refund works', 'refund steps', 'refund on chain', 'automatic refund'],
    question: 'How does the refund process work technically?',
    answer:
      'Refund flow:\n\n**User-requested refund:**\n1. Create a support ticket (category: Refund)\n2. Admin reviews your request\n3. If approved, admin initiates the refund from the Admin panel\n4. PGT tokens are transferred back to your wallet on-chain\n5. Ticket NFT may be burned or marked as refunded\n\n**Automatic refund (event cancelled):**\n1. Organizer marks event as cancelled\n2. Smart contract processes refunds automatically\n3. PGT returns to all ticket holders within 48 hours\n4. No action needed from your side\n\nYou can track refund status in your Support Tickets.',
    category: 'refund',
  },

  // ═══════════════════════════════════════════════════
  // ─── UPGRADEABLE CONTRACTS ────────────────────────
  // ═══════════════════════════════════════════════════
  {
    id: 'contract-upgrades',
    keywords: ['upgrade', 'contract upgrade', 'upgradeable', 'new version', 'platform update', 'smart contract update'],
    question: 'Can the smart contracts be upgraded?',
    answer:
      'Yes, the platform uses upgradeable smart contracts (UUPS proxy pattern):\n• **Seamless** — Upgrades happen without affecting your tickets or PGT\n• **Your NFTs are safe** — Token ownership is preserved across upgrades\n• **New features** — Upgrades can add new functionality\n• **Admin only** — Only authorized admins can perform upgrades\n• **Transparent** — All upgrades are visible on BaseScan\n\nYou don\'t need to do anything during an upgrade — your tickets, PGT, and data remain intact.',
    category: 'technical',
  },

  // ═══════════════════════════════════════════════════
  // ─── MERCHANT PAYMENT FLOW ────────────────────────
  // ═══════════════════════════════════════════════════
  {
    id: 'merchant-accept-payment',
    keywords: ['accept payment', 'merchant payment', 'vendor payment', 'accept pgt at event', 'sell at event'],
    question: 'How do I accept PGT payments as a merchant?',
    answer:
      'Complete merchant payment flow:\n\n1. **Setup once:**\n   • Get a merchant license\n   • Install the POS app\n   • Configure products and prices\n\n2. **Taking a payment:**\n   • Add items to the order (or enter amount)\n   • Tap **Request Payment**\n   • QR code appears on your screen\n   • Customer scans with their wallet app\n   • Customer approves the PGT amount\n   • ✅ Payment confirmed — receipt generated\n\n3. **Track earnings:**\n   • Real-time sales on the POS Dashboard\n   • End-of-day reports in Merchant Admin',
    category: 'event_inquiry',
  },

  // ═══════════════════════════════════════════════════
  // ─── PROFILE & ACCOUNT ────────────────────────────
  // ═══════════════════════════════════════════════════
  {
    id: 'profile',
    keywords: ['profile', 'edit profile', 'my profile', 'account settings', 'display name', 'user profile'],
    question: 'How do I set up my profile?',
    answer:
      'On the mobile app:\n1. Go to **Settings** → **Profile**\n2. Set your display name\n3. Add a profile picture (optional)\n4. Save changes\n\nYour profile is linked to your wallet address. Completing your profile earns **50 XP**!\n\nOn the web app, your identity is your wallet address. The app may show your ENS name or Basename if you have one.',
    category: 'account',
  },
  {
    id: 'basename-ens',
    keywords: ['basename', 'ens', 'ens name', 'base name', 'domain name', 'wallet name', '.eth', '.base'],
    question: 'What is a Basename / ENS name?',
    answer:
      'Basenames and ENS names are human-readable names for your wallet:\n• **ENS** — e.g., yourname.eth (Ethereum Name Service)\n• **Basename** — e.g., yourname.base (Base network names)\n\n**Benefits:**\n• Shows your name instead of 0x... address in the app\n• Easier for others to send you tokens\n• Works as your Web3 identity\n\nIf you own a Basename or ENS name, the app automatically resolves and displays it.',
    category: 'account',
  },

  // ═══════════════════════════════════════════════════
  // ─── ABOUT THE APP & ECOSYSTEM ────────────────────
  // ═══════════════════════════════════════════════════
  {
    id: 'about-app',
    keywords: ['about', 'about app', 'version', 'app version', 'who made this', 'developers'],
    question: 'About this app',
    answer:
      'This is the **PGT Ecosystem** — a comprehensive blockchain-based event ticketing and commerce platform.\n\n**Built with:**\n• Solidity smart contracts on Base (Ethereum L2)\n• React web application\n• React Native mobile app (iOS + Android)\n• Node.js backend server\n\n**Key features:**\n• NFT ticketing with QR check-in\n• PGT utility token\n• Marketplace for ticket resale\n• POS system for merchants\n• Loyalty, XP, and coupon systems\n• Ambassador/referral program\n• Support ticket system with AI chat\n\nFor version info, check Settings → About.',
    category: 'other',
  },

  // ═══════════════════════════════════════════════════
  // ─── SOUND & ACCESSIBILITY ────────────────────────
  // ═══════════════════════════════════════════════════
  {
    id: 'pos-sounds',
    keywords: ['pos sound', 'payment sound', 'notification sound', 'sound notifications', 'pos beep', 'audio alert'],
    question: 'Does the POS terminal have sound notifications?',
    answer:
      'Yes! The POS terminal uses audio notifications:\n• **Payment received** — Success chime when payment is confirmed\n• **Payment failed** — Alert sound if payment fails\n• **New order** — Notification when a new order comes in\n\nSound settings can be configured in POS Settings → Notifications. You can mute or adjust volume.',
    category: 'event_inquiry',
  },

  // ═══════════════════════════════════════════════════
  // ─── SETTINGS SYNC ────────────────────────────────
  // ═══════════════════════════════════════════════════
  {
    id: 'settings-sync',
    keywords: ['sync settings', 'settings across devices', 'pos sync', 'save settings', 'settings backup'],
    question: 'Do my settings sync across devices?',
    answer:
      'Settings sync varies by app:\n\n**POS Terminal:**\n• Settings sync across terminals linked to the same merchant wallet\n• Product catalog, prices, and configuration are shared\n\n**Mobile App:**\n• Wallet data is on-chain, so it\'s available everywhere\n• App preferences (language, currency, notifications) are device-local\n\n**Web App:**\n• Wallet connection is per-browser\n• On-chain data (tickets, PGT, XP) is available on any device when you connect\n\nYour blockchain data is always consistent — it\'s the app preferences that are per-device.',
    category: 'technical',
  },

  // ═══════════════════════════════════════════════════
  // ─── ERROR MESSAGES ───────────────────────────────
  // ═══════════════════════════════════════════════════
  {
    id: 'common-errors',
    keywords: ['error message', 'error code', 'reverted', 'insufficient funds', 'user rejected', 'execution reverted'],
    question: 'What do common error messages mean?',
    answer:
      'Common error messages and fixes:\n\n• **"Insufficient funds"** — Not enough PGT or ETH in your wallet\n• **"User rejected transaction"** — You clicked "Reject" in MetaMask\n• **"Execution reverted"** — Smart contract rejected the operation (sold out, expired, etc.)\n• **"Nonce too high"** — Reset MetaMask: Settings → Advanced → Reset Account\n• **"Gas estimation failed"** — Transaction would fail on-chain; check your inputs\n• **"Network error"** — Check internet connection and try refreshing\n• **"Ticket not found"** — Make sure you\'re on the correct network (Base)\n\nFor any error not listed here, include the exact message in your support ticket.',
    category: 'technical',
  },
  {
    id: 'user-rejected',
    keywords: ['rejected transaction', 'user rejected', 'cancelled transaction', 'denied transaction', 'i rejected by mistake'],
    question: 'I accidentally rejected a transaction',
    answer:
      'If you rejected/cancelled a transaction in your wallet:\n• **No funds were taken** — Rejecting cancels the operation before it reaches the blockchain\n• **Try again** — Simply repeat the action (buy ticket, swap, etc.)\n• **No penalty** — There\'s no fee for rejecting\n\nThis is different from a *failed* transaction — if a transaction fails after you confirmed, gas was spent but the operation didn\'t complete.',
    category: 'technical',
  },

  // ═══════════════════════════════════════════════════
  // ─── DEV / SCANNER SIMULATOR ──────────────────────
  // ═══════════════════════════════════════════════════
  {
    id: 'test-scanner',
    keywords: ['test scanner', 'scanner simulator', 'dev scanner', 'test check in', 'simulate scan'],
    question: 'How do I test the scanner?',
    answer:
      'For development and testing, a scanner simulator is available:\n1. Enable **Dev Mode** in settings (if available)\n2. The **Dev Scanner Simulator** appears on the ticket page\n3. Click to simulate a QR scan and check-in\n\nThis is for testing only and not available in production. For real events, use the **Staff Scanner** app.',
    category: 'technical',
  },

  // ═══════════════════════════════════════════════════
  // ─── DEX LIQUIDITY ────────────────────────────────
  // ═══════════════════════════════════════════════════
  {
    id: 'dex-liquidity',
    keywords: ['liquidity', 'dex', 'liquidity pool', 'provide liquidity', 'liquidity provider', 'lp tokens'],
    question: 'What is the DEX liquidity system?',
    answer:
      'The DEX (Decentralized Exchange) provides the liquidity for PGT swaps:\n• **Liquidity pool** — ETH/PGT pair that enables token swapping\n• **Pricing** — Swap rates are determined by the pool ratio (automated market maker)\n• **DEX License** — Licensed providers can contribute liquidity\n• **Fees** — Swap fees are distributed to liquidity providers\n\nThe DEXLiquidityLicense smart contract manages who can provide liquidity and under what terms.',
    category: 'account',
  },
];

export default knowledgeBase;

/**
 * Quick-reply suggestions shown when chat opens.
 */
export const quickReplies = [
  'How do I buy a ticket?',
  'I need a refund',
  'My ticket is not showing',
  'How do I connect my wallet?',
  'How does the marketplace work?',
  'Talk to support team',
];

/**
 * Category-based quick replies for contextual suggestions.
 */
export const categoryReplies = {
  tickets: ['How do I buy a ticket?', 'Where are my tickets?', 'Can I transfer a ticket?', 'Marketplace info'],
  refund: ['How do I get a refund?', 'How long does a refund take?', 'Partial refund?'],
  account: ['Connect wallet help', 'Wrong network', 'What is PGT?', 'How to buy PGT'],
  event_inquiry: ['Event details', 'QR code help', 'XP system', 'Loyalty rewards'],
  technical: ['Transaction failed', 'Gas fees', 'App loading slowly', 'Browser support'],
  other: ['Talk to support team', 'What is this platform?', 'Security info'],
};
